/**
 * TUVI - Ficha FFE
 * Muestra cotizaciones firmadas y sus actividades FFE con imagenes de Google Drive
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { matchesAnyField, normalize } from '../../utils/searchHelper';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import client from '../../services/graphqlClient';
import { BACKEND_URL, UPLOAD_URL } from '../../config';
import { getTipoPlano, ordenTipoPlano } from '../Planos/tiposPlano';
import FichaFFEPDF from './FichaFFEPDF';
import ContratistaPDF from './ContratistaPDF';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import CatalogoFFE from '../CatalogoFFE/CatalogoFFE';
import PlanosGeneradosTab from '../Planos/PlanosGeneradosTab';
import AdjuntosFfeTab from './AdjuntosFfeTab';
import './FichaFFE.css';

// Cola global de carga de imágenes (máx 2 simultáneas)
const imageQueue = { pending: [], active: 0, maxConcurrent: 3 };
function processQueue() {
  while (imageQueue.active < imageQueue.maxConcurrent && imageQueue.pending.length > 0) {
    const next = imageQueue.pending.shift();
    next();
  }
}
function enqueueImage(loadFn) {
  return new Promise((resolve) => {
    const execute = () => {
      imageQueue.active++;
      loadFn().finally(() => {
        imageQueue.active--;
        resolve();
        processQueue();
      });
    };
    if (imageQueue.active < imageQueue.maxConcurrent) {
      execute();
    } else {
      imageQueue.pending.push(execute);
    }
  });
}

// Componente de imagen con lazy load + cola + reintentos infinitos hasta cargar
const LazyProxyImage = ({ src, alt, className, onError }) => {
  const [loaded, setLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!src) return;
    cancelledRef.current = false;
    setLoaded(false);
    setImgSrc(null);
    setRetrying(false);

    const el = imgRef.current;
    if (!el) return;

    const intentarCargar = (intento) => {
      if (cancelledRef.current) return;
      if (intento > 0) setRetrying(true);
      enqueueImage(() => {
        return new Promise((resolve) => {
          if (cancelledRef.current) { resolve(); return; }
          const img = new Image();
          const timeout = setTimeout(() => {
            img.src = ''; // Abort
            resolve();
            if (!cancelledRef.current) {
              const delay = Math.min(1500 + (intento * 500), 6000);
              setTimeout(() => intentarCargar(intento + 1), delay);
            }
          }, 15000); // 15s timeout per attempt
          img.onload = () => {
            clearTimeout(timeout);
            if (!cancelledRef.current) { setImgSrc(src); setLoaded(true); setRetrying(false); }
            resolve();
          };
          img.onerror = () => {
            clearTimeout(timeout);
            resolve();
            if (!cancelledRef.current) {
              // Reintentar siempre - hasta 20 intentos con delays crecientes
              if (intento < 20) {
                const delay = Math.min(1000 + (intento * 800), 8000);
                setTimeout(() => intentarCargar(intento + 1), delay);
              }
            }
          };
          // Añadir cache-buster en reintentos para evitar cache del 403
          const separator = src.includes('?') ? '&' : '?';
          img.src = intento > 0 ? src + separator + '_r=' + intento + '&_t=' + Date.now() : src;
        });
      });
    };

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observerRef.current?.disconnect();
          intentarCargar(0);
        }
      },
      { rootMargin: '500px' } // Precargar con más anticipación
    );
    observerRef.current.observe(el);

    return () => { cancelledRef.current = true; observerRef.current?.disconnect(); };
  }, [src]);

  if (!loaded) return (
    <div ref={imgRef} className="ffe-imagen-placeholder" style={{ opacity: retrying ? 0.7 : 0.5 }}>
      <span className="ffe-placeholder-text">{retrying ? '⏳ Reintentando...' : 'Cargando...'}</span>
    </div>
  );
  return <img ref={imgRef} src={imgSrc} alt={alt} className={className} onError={onError} />;
};

// Convierte link de Google Drive de formato compartido a formato directo de imagen
const convertirLinkDrive = (url) => {
  if (!url) return url;
  const match = url.match(/\/file\/d\/([^/?]+)/);
  if (match) return `https://drive.google.com/uc?export=view&id=${match[1]}`;
  return url;
};

// Formatear moneda colombiana
const formatCurrency = (value) => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const FichaFFE = ({ embebidoCotId, embebidoSource }) => {
  const { success, error } = useNotification();
  const { tienePermiso } = useAuth();
  const permisoTabFicha = tienePermiso('documentos', 'ffe-tab-ficha');
  const permisoTabContratistas = tienePermiso('documentos', 'ffe-tab-contratistas');
  const permisoTabCarrito = tienePermiso('documentos', 'ffe-tab-carrito');
  const permisoEditarComercial = tienePermiso('documentos', 'ffe-editar-comercial');
  const [listaComerciales, setListaComerciales] = useState([]);
  const [editandoComercialId, setEditandoComercialId] = useState(null); // `${source}-${id}`

  const exportarExcelFFE = () => {
    if (cotizaciones.length === 0) { error('No hay fichas para exportar'); return; }

    const datos = cotizaciones.map(cot => ({
      'Consecutivo': 'COT-' + String(cot.consecutivo).padStart(4, '0'),
      'Cliente': cot.nombre_cliente,
      'Proyecto': cot.proyecto_nombre,
      'Apartamento': cot.apartamento || '',
      'Fecha': cot.fecha,
      'Valor Final': parseFloat(cot.valor_mostrar) || 0,
      'Comercial': cot.comercial || '',
      'Act. FFE': cot.total_ffe || 0,
      'Avance': cot.avance_ffe === 'agendado' ? 'Agendado' : cot.avance_ffe === 'en_elaboracion' ? 'En elaboración' : cot.avance_ffe === 'en_revision' ? 'En revisión' : 'Por agendar',
      'Llaves': parseInt(cot.ffe_llaves) ? '✓' : '✗',
      'Pago': parseInt(cot.ffe_pago) ? '✓' : '✗',
      'Firma': parseInt(cot.ffe_firma) ? '✓' : '✗',
      'Enviado a Proyectos': parseInt(cot.ffe_enviado_proyecto) ? 'Sí' : 'No',
      'Estado': parseInt(cot.ffe_enviado_proyecto) ? 'Exportado' : 'Pendiente'
    }));

    const ws = XLSX.utils.json_to_sheet(datos);
    ws['!cols'] = [
      { wch: 14 }, { wch: 25 }, { wch: 22 }, { wch: 12 }, { wch: 12 },
      { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 18 },
      { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 18 }, { wch: 12 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fichas FFE');
    XLSX.writeFile(wb, `Fichas_FFE_TUVI_${new Date().toISOString().slice(0, 10)}.xlsx`);
    success('Excel exportado');
  };

  // Vista: 'lista' o 'detalle'
  const [vista, setVista] = useState('lista');
  const [modoLectura, setModoLectura] = useState(false);
  const [loading, setLoading] = useState(false);

  // Lista de cotizaciones firmadas
  const [cotizaciones, setCotizaciones] = useState([]);
  const [tabLista, setTabLista] = useState('pendientes'); // 'pendientes' | 'exportados'
  const [busquedaFFE, setBusquedaFFE] = useState('');
  // Filtro por comercial (menú en el encabezado de la columna)
  const [filtroComercial, setFiltroComercial] = useState('');
  const [menuComercialAbierto, setMenuComercialAbierto] = useState(false);

  // Cerrar el menú de comercial al hacer clic en cualquier otro lugar
  useEffect(() => {
    if (!menuComercialAbierto) return;
    const cerrar = () => setMenuComercialAbierto(false);
    document.addEventListener('click', cerrar);
    return () => document.removeEventListener('click', cerrar);
  }, [menuComercialAbierto]);

  // Al cambiar de pestaña (Pendientes/Exportados) se limpia el filtro de comercial
  useEffect(() => { setFiltroComercial(''); setMenuComercialAbierto(false); }, [tabLista]);

  // Detalle: cotización seleccionada + actividades
  const [cotizacionActual, setCotizacionActual] = useState(null);
  const [actividades, setActividades] = useState([]);
  const [actividadesTodas, setActividadesTodas] = useState([]); // Todas las contratadas (para Contratistas)

  // Edición de imagen por actividad
  const [editandoActividad, setEditandoActividad] = useState(null);
  const [formImagen, setFormImagen] = useState({ nombre: '', codigo: '', url_imagen: '' });
  const [catalogoModal, setCatalogoModal] = useState(null); // actividadId when open
  const [catalogoPlanoModal, setCatalogoPlanoModal] = useState(null); // { planoIdx, urlIdx } when open
  const [guardando, setGuardando] = useState(false);

  // Edición inline de campos (descripcion, observaciones, proveedor)
  const [editandoCampo, setEditandoCampo] = useState(null); // { id: actividadId, campo: 'descripcion_custom'|'observaciones'|'proveedor' }
  const [campoTemp, setCampoTemp] = useState('');

  // Edición de cantidad por actividad
  const [editandoCantidad, setEditandoCantidad] = useState(null);
  const [cantidadTemp, setCantidadTemp] = useState('');
  const [editandoDescAct, setEditandoDescAct] = useState(null); // actividad_id en edición de descripción
  const [descActTemp, setDescActTemp] = useState('');

  // Pestaña activa en detalle: 'ficha' o 'contratistas'
  const [tabActiva, setTabActiva] = useState('ficha');

  // Filtros del tablero de indicadores: zona y estado de avance.
  const [filtroZona, setFiltroZona] = useState('');      // '' = todas | '__sin__' = sin zona
  const [filtroAvance, setFiltroAvance] = useState(null); // null = sin filtro de estado
  const [zonasFFE, setZonasFFE] = useState([]);
  // Panel visible: 'edicion' o 'preview'
  const [vistaPanel, setVistaPanel] = useState('edicion');

  // Helper: cada plano debe tener un UUID estable para vincularse con
  // planos_generados (editor de planos estilo Canva). Si ya tiene, se conserva.
  const ensureUuid = (p) => (p && p.id ? p : { ...(p || {}), id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` });

  // Planos compartidos (FFE y Contratistas usan los mismos)
  const [planos, setPlanos] = useState([
    ensureUuid({ titulo: 'PLANO GENERALIDADES', urls: [''] }),
    ensureUuid({ titulo: 'PLANO MUROS NUEVOS Y DEMOLICIONES', urls: [''] }),
  ]);
  const [consideraciones, setConsideraciones] = useState('');
  const [descargando, setDescargando] = useState(false);
  // Planos generados (del editor) para reflejarlos en la Ficha FFE por su nombre
  const [planosGen, setPlanosGen] = useState([]);
  const fichaRef = useRef(null);
  // Contratistas - residente
  const [residente, setResidente] = useState({ nombre: '', contacto: '', empresa: 'TUVI' });
  const [listaResidentes, setListaResidentes] = useState([]);
  const [busquedaResidente, setBusquedaResidente] = useState('');
  const [mostrarDropdownResidente, setMostrarDropdownResidente] = useState(false);
  const dropdownResidenteRef = useRef(null);
  const [descargandoContr, setDescargandoContr] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const contratistaRef = useRef(null);

  // Lista de proveedores (clasificacion='proveedor') para dropdown
  const [listaProveedores, setListaProveedores] = useState([]);

  // Modal de enchapes para observaciones
  const [listaEnchapes, setListaEnchapes] = useState([]);
  const [modalEnchape, setModalEnchape] = useState(null); // actividadId cuando está abierto
  const [busquedaEnchape, setBusquedaEnchape] = useState('');

  // Modal confirmación envío a proyectos
  const [modalEnvioProyecto, setModalEnvioProyecto] = useState(null); // { cotizacionId, campo }

  // Formulas carrito (desde BD)
  const [formulasDB, setFormulasDB] = useState(null);

  // Carrito de compras
  const [carritoData, setCarritoData] = useState({
    pega: 25,
    pegaManual: false,
    lechadas: [
      { color: '', ubicacion: '' },
      { color: '', ubicacion: '' },
      { color: '', ubicacion: '' },
      { color: '', ubicacion: '' }
    ],
    observacionesCarrito: {},
    checks: {
      pega: false,           // check único para pega
      lechadas: [],          // [bool] por cada lechada
      enchapes: {},          // { enchape_texto: bool }
      actividades: {},       // { actividad_id: bool }
      carpinteria: false     // check único para toda carpintería
    }
  });
  const [guardandoCarrito, setGuardandoCarrito] = useState(false);

  // Funciones de planos (compartidas FFE + Contratistas)
  const actualizarPlanoTitulo = (idx, valor) => {
    setPlanos(prev => prev.map((p, i) => i === idx ? { ...p, titulo: valor } : p));
  };

  const actualizarPlanoUrl = (planoIdx, urlIdx, valor) => {
    setPlanos(prev => prev.map((p, i) => {
      if (i !== planoIdx) return p;
      const nuevasUrls = [...p.urls];
      nuevasUrls[urlIdx] = convertirLinkDrive(valor);
      return { ...p, urls: nuevasUrls };
    }));
  };

  const agregarUrlAPlano = (planoIdx) => {
    setPlanos(prev => prev.map((p, i) => {
      if (i !== planoIdx) return p;
      return { ...p, urls: [...p.urls, ''] };
    }));
  };

  const eliminarUrlDePlano = (planoIdx, urlIdx) => {
    setPlanos(prev => prev.map((p, i) => {
      if (i !== planoIdx) return p;
      if (p.urls.length <= 1) return p;
      return { ...p, urls: p.urls.filter((_, ui) => ui !== urlIdx) };
    }));
  };

  const agregarPlano = () => {
    setPlanos(prev => [...prev, ensureUuid({ titulo: `PLANO ${prev.length + 1}`, urls: [''] })]);
  };

  const eliminarPlano = (idx) => {
    if (planos.length <= 1) return;
    setPlanos(prev => prev.filter((_, i) => i !== idx));
  };

  // Cargar lista de residentes del módulo Residentes
  const cargarResidentes = useCallback(async () => {
    try {
      const data = await client.query(`{ residentes { id nombre telefono } }`);
      setListaResidentes(data.residentes || []);
    } catch (err) {
      // Silencioso - no es crítico
    }
  }, []);

  useEffect(() => {
    cargarResidentes();
  }, [cargarResidentes]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownResidenteRef.current && !dropdownResidenteRef.current.contains(e.target)) {
        setMostrarDropdownResidente(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const seleccionarResidente = (res) => {
    setResidente(prev => ({ ...prev, nombre: res.nombre, contacto: res.telefono || '' }));
    setBusquedaResidente(res.nombre);
    setMostrarDropdownResidente(false);
  };

  const residentesFiltrados = listaResidentes.filter(r =>
    r.nombre.toLowerCase().includes(busquedaResidente.toLowerCase())
  );

  // Descargar PDF Contratistas
  const descargarPDFContratistas = async () => {
    if (!contratistaRef.current) return;
    setDescargandoContr(true);
    try {
      const canvas = await html2canvas(contratistaRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff'
      });

      const imgWidth = 612;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [imgWidth, imgHeight]
      });

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);
      const nombre = `Especificaciones_${cotizacionActual?.proyecto_nombre || 'doc'}_${cotizacionActual?.apartamento || ''}.pdf`;
      pdf.save(nombre.replace(/\s+/g, '_'));
      success('PDF Contratistas descargado');
    } catch (err) {
      console.error('Error generando PDF:', err);
      error('Error al generar el PDF');
    } finally {
      setDescargandoContr(false);
    }
  };

  // Descargar PDF Ficha FFE
  const descargarPDF = async () => {
    if (!fichaRef.current) return;
    setDescargando(true);
    try {
      const canvas = await html2canvas(fichaRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#f7f3e7'
      });

      const imgWidth = 612;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF({
        orientation: imgHeight > imgWidth * 1.5 ? 'portrait' : 'portrait',
        unit: 'pt',
        format: [imgWidth, imgHeight]
      });

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);
      const nombre = `Ficha_FFE_${cotizacionActual?.proyecto_nombre || 'doc'}_${cotizacionActual?.apartamento || ''}.pdf`;
      pdf.save(nombre.replace(/\s+/g, '_'));
      success('PDF descargado');
    } catch (err) {
      console.error('Error generando PDF:', err);
      error('Error al generar el PDF');
    } finally {
      setDescargando(false);
    }
  };

  // Descargar PDF paginado para imprimir (sin cortar contenido)
  const descargarPDFPaginado = async (tipo) => {
    const ref = tipo === 'ficha' ? fichaRef.current : contratistaRef.current;
    if (!ref) return;
    tipo === 'ficha' ? setDescargando(true) : setDescargandoContr(true);
    setShowPdfModal(false);

    try {
      const bgColor = tipo === 'ficha' ? '#f7f3e7' : '#ffffff';

      // Recopilar todos los bloques atómicos (que no se deben cortar)
      // Orden: header, planos, cada categoría con sus filas (tr), observaciones, consideraciones, footer
      const bloques = [];

      // Header
      const header = ref.querySelector('.ffe-pdf-header-area');
      if (header) bloques.push(header);

      // Planos individuales
      const planosEl = ref.querySelectorAll('.ffe-pdf-plano, .ffe-pdf-plano-wide');
      planosEl.forEach(p => bloques.push(p));

      // Categorías de actividades: cada categoría como bloque (header + tabla completa)
      const categorias = ref.querySelectorAll('.ffe-pdf-categoria');
      categorias.forEach(cat => {
        // Capturar header de categoría + thead juntos
        const catHeader = cat.querySelector('.ffe-pdf-cat-header');
        if (catHeader) bloques.push(catHeader);

        // Cada fila de actividad individualmente (no se corta a mitad)
        const tabla = cat.querySelector('.ffe-pdf-cat-table');
        if (tabla) {
          const thead = tabla.querySelector('thead');
          if (thead) bloques.push(thead);
          const filas = tabla.querySelectorAll('tbody tr');
          filas.forEach(tr => bloques.push(tr));
        }
      });

      // Observaciones
      const obs = ref.querySelector('.ffe-pdf-observaciones');
      if (obs) bloques.push(obs);

      // Consideraciones
      const consid = ref.querySelector('.ffe-pdf-consideraciones');
      if (consid) bloques.push(consid);

      // Footer
      const footer = ref.querySelector('.ffe-pdf-footer');
      if (footer) bloques.push(footer);

      // Si no encontró bloques, usar el ref completo dividido
      if (bloques.length === 0) {
        bloques.push(ref);
      }

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
      const pageW = 612;
      const pageH = 792;
      const margin = 25;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      let currentY = 0;
      let firstPage = true;
      const canvasCache = [];

      // Pre-renderizar todos los bloques
      for (const el of bloques) {
        try {
          const canvas = await html2canvas(el, {
            scale: 3, useCORS: true, allowTaint: false, backgroundColor: bgColor,
            scrollX: 0, scrollY: 0, imageTimeout: 15000
          });
          const imgW = usableW;
          const imgH = (canvas.height * imgW) / canvas.width;
          canvasCache.push({ canvas, imgW, imgH });
        } catch (e) {
          // Skip elementos que no se pueden renderizar
        }
      }

      // Distribuir en páginas sin cortar
      for (const item of canvasCache) {
        const { canvas, imgW, imgH } = item;

        // Si el bloque no cabe en el espacio restante de la página actual
        if (currentY + imgH > usableH && currentY > 0) {
          pdf.addPage();
          currentY = 0;
        }

        // Si es la primera página y no hemos agregado nada
        if (firstPage) {
          firstPage = false;
        }

        // Si un solo bloque es más alto que una página, dividirlo
        if (imgH > usableH) {
          const totalSlices = Math.ceil(imgH / usableH);
          for (let s = 0; s < totalSlices; s++) {
            if (s > 0 || currentY > 0) { pdf.addPage(); currentY = 0; }
            const srcY = (s * usableH / imgH) * canvas.height;
            const srcH = Math.min((usableH / imgH) * canvas.height, canvas.height - srcY);
            const destH = (srcH / canvas.height) * imgH;
            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = canvas.width;
            cropCanvas.height = Math.round(srcH);
            cropCanvas.getContext('2d').drawImage(canvas, 0, Math.round(srcY), canvas.width, Math.round(srcH), 0, 0, canvas.width, Math.round(srcH));
            pdf.addImage(cropCanvas.toDataURL('image/png'), 'PNG', margin, margin, imgW, destH);
            currentY = destH;
          }
        } else {
          pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin + currentY, imgW, imgH);
          currentY += imgH;
        }
      }

      const nombre = tipo === 'ficha'
        ? `Ficha_FFE_IMPRIMIR_${cotizacionActual?.proyecto_nombre || 'doc'}_${cotizacionActual?.apartamento || ''}.pdf`
        : `Especificaciones_IMPRIMIR_${cotizacionActual?.proyecto_nombre || 'doc'}_${cotizacionActual?.apartamento || ''}.pdf`;
      pdf.save(nombre.replace(/\s+/g, '_'));
      success('PDF para imprimir descargado');
    } catch (err) {
      console.error('Error generando PDF paginado:', err);
      error('Error al generar el PDF');
    } finally {
      setDescargando(false);
      setDescargandoContr(false);
    }
  };

  // AbortController para cancelar requests
  const abortRef = useRef(null);

  // Cargar cotizaciones firmadas (migrado de PHP a GraphQL)
  const cargarCotizaciones = useCallback(async () => {
    setLoading(true);
    try {
      const data = await client.query(`{ ffeCotizaciones { id consecutivo nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 fecha subtotal aiu_valor iva_valor descuento valor_final valor_final_descuento valor_mostrar estado source ffe_llaves ffe_pago ffe_firma ffe_enviado_proyecto ffe_ficha_vacia avance_ffe zona total_ffe estado_compra fecha_probable_inicio proyecto_id comercial bano_social_alto bano_social_ancho bano_ppal_alto bano_ppal_ancho balcon altura_ducha } }`);
      setCotizaciones(data.ffeCotizaciones || []);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error:', err);
        error('Error de conexión al cargar cotizaciones');
      }
    } finally {
      setLoading(false);
    }
  }, [error]);

  // Cargar actividades FFE de una cotización + todas las contratadas
  const cargarActividades = useCallback(async (cotizacionId, source = 'nueva') => {
    setLoading(true);
    try {
      // Cargar actividades FFE via GraphQL
      const dataFFE = await client.query(`query($cid: Int!, $src: String!) { ffeActividades(cotizacion_id: $cid, source: $src) { id actividad_id descripcion unidad cantidad valor_unitario valor_total espacio capitulo enchape activo_matriz imagen_nombre imagen_codigo url_imagen descripcion_custom observaciones proveedor } }`, { cid: cotizacionId, src: source });

      // Buscar la cotizacion en la lista ya cargada para obtener datos del inmueble
      const cotEnLista = cotizaciones.find(c => c.id === cotizacionId && c.source === source);
      if (cotEnLista) {
        setCotizacionActual({ ...cotEnLista, source });
      } else {
        // En modo embebido la lista puede no estar cargada aún, obtener datos directamente
        // Se piden TODOS los campos que usa el Carrito de Compras y demás pestañas.
        try {
          if (source === 'vieja') {
            const d = await client.query(`query($id: Int!) { cotizacionVieja(id: $id) { id consecutivo nombre_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 habitaciones banos subtotal aiu_valor iva_valor valor_final estado avance_ffe proyecto_id } }`, { id: cotizacionId });
            if (d.cotizacionVieja) setCotizacionActual({ ...d.cotizacionVieja, source, valor_mostrar: Number(d.cotizacionVieja.valor_final || 0) });
          } else {
            const d = await client.query(`query($id: Int!) { cotizacion(id: $id) { id consecutivo nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 habitaciones banos subtotal aiu_valor iva_valor descuento valor_final valor_final_descuento estado avance_ffe ffe_ficha_vacia proyecto_id bano_social_alto bano_social_ancho bano_ppal_alto bano_ppal_ancho balcon altura_ducha } }`, { id: cotizacionId });
            if (d.cotizacion) {
              const c = d.cotizacion;
              setCotizacionActual({
                ...c, source,
                valor_mostrar: Number(c.valor_final || 0) - Number(c.descuento || 0),
              });
            }
          }
        } catch {}
      }
      setActividades(dataFFE.ffeActividades || []);

      // Cargar todas las actividades (para Contratistas) via GraphQL
      try {
        if (source === 'vieja') {
          const todosData = await client.query(`query($id: Int!) { cotizacionVieja(id: $id) { actividades { id descripcion unidad cantidad valor_unitario valor_total categoria } } }`, { id: cotizacionId });
          setActividadesTodas(todosData.cotizacionVieja?.actividades || []);
        } else {
          const todosData = await client.query(`query($id: Int!) { cotizacion(id: $id) { actividades { id descripcion unidad cantidad valor_unitario valor_total capitulo categoria espacio } } }`, { id: cotizacionId });
          setActividadesTodas(todosData.cotizacion?.actividades || []);
        }
      } catch { setActividadesTodas([]); }
    } catch (err) {
      console.error('Error:', err);
      error('Error al cargar actividades FFE');
    } finally {
      setLoading(false);
    }
  }, [error, cotizaciones]);

  // Cargar proveedores con clasificacion='proveedor' para dropdown
  const cargarProveedores = useCallback(async () => {
    try {
      const data = await client.query(`{ proveedores(clasificacion: "proveedor") { proveedores { id nombre } } }`);
      setListaProveedores(data.proveedores.proveedores || []);
    } catch (err) {
      console.error('Error cargando proveedores:', err);
    }
  }, []);

  // Cargar lista de enchapes para modal
  const cargarEnchapes = useCallback(async () => {
    try {
      const data = await client.query(`{ enchapes { id ref color formato nombre referencia proveedor_id proveedor_nombre } }`);
      setListaEnchapes(data.enchapes || []);
    } catch (err) {
      console.error('Error cargando enchapes:', err);
    }
  }, []);

  // Cargar lista de comerciales (usuarios activos) para dropdown de edición
  const cargarComerciales = useCallback(async () => {
    try {
      const data = await client.query(`{ comercialesFFE { id nombre } }`);
      setListaComerciales(data.comercialesFFE || []);
    } catch (err) {
      console.error('Error cargando comerciales:', err);
    }
  }, []);

  // Guardar comercial (override manual)
  const guardarComercialFFE = async (cotId, source, nuevoNombre) => {
    // Actualización optimista
    const original = cotizaciones.find(c => c.id === cotId && c.source === source)?.comercial;
    setCotizaciones(prev => prev.map(c =>
      (c.id === cotId && c.source === source) ? { ...c, comercial: nuevoNombre || null } : c
    ));
    setEditandoComercialId(null);
    try {
      const r = await client.mutate(
        `mutation($cid: Int!, $src: String!, $com: String) { actualizarComercialFFE(cotizacion_id: $cid, source: $src, comercial: $com) { success message } }`,
        { cid: cotId, src: source, com: nuevoNombre || null }
      );
      if (!r.actualizarComercialFFE.success) {
        setCotizaciones(prev => prev.map(c =>
          (c.id === cotId && c.source === source) ? { ...c, comercial: original } : c
        ));
        error(r.actualizarComercialFFE.message || 'Error al actualizar comercial');
      }
    } catch (err) {
      setCotizaciones(prev => prev.map(c =>
        (c.id === cotId && c.source === source) ? { ...c, comercial: original } : c
      ));
      error('Error al actualizar comercial');
    }
  };

  useEffect(() => {
    cargarCotizaciones();
    cargarProveedores();
    cargarEnchapes();
    cargarComerciales();
    // Zonas para el filtro del tablero de indicadores
    client.query('{ zonas { id nombre } }').then(d => setZonasFFE(d.zonas || [])).catch(() => {});
    // Cargar formulas del carrito desde BD
    client.query(`{ formulasCarrito { clave coeficiente } }`).then(d => {
      if (d.formulasCarrito) {
        const map = {};
        d.formulasCarrito.forEach(f => { map[f.clave] = f.coeficiente; });
        setFormulasDB(map);
      }
    }).catch(() => {});
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [cargarCotizaciones, cargarProveedores, cargarEnchapes]);

  // Auto-abrir en modo embebido (dentro de un proyecto)
  useEffect(() => {
    if (embebidoCotId && embebidoSource) {
      setModoLectura(true);
      setVista('detalle');
      const tabDefault = permisoTabFicha ? 'ficha' : permisoTabContratistas ? 'contratistas' : permisoTabCarrito ? 'carrito' : 'ficha';
      setTabActiva(tabDefault);
      cargarActividades(embebidoCotId, embebidoSource);
      cargarConfig(embebidoCotId, embebidoSource);
      cargarCarrito(embebidoCotId, embebidoSource);
      cargarPlanosGen(embebidoCotId);
    }
  }, [embebidoCotId, embebidoSource]);

  // Cargar configuración guardada (planos, consideraciones, residente)
  const cargarConfig = useCallback(async (cotizacionId, source = 'nueva') => {
    try {
      const data = await client.query(`query($cid: Int!, $src: String) { fichaFfeConfig(cotizacion_id: $cid, source: $src) { planos consideraciones residente_nombre residente_contacto residente_empresa } }`, { cid: cotizacionId, src: source });
      const config = data.fichaFfeConfig;
      if (config) {
        if (config.planos) {
          try {
            const parsed = JSON.parse(config.planos);
            if (parsed.length > 0) setPlanos(parsed.map(ensureUuid));
          } catch(e) {}
        }
        if (config.consideraciones != null) {
          // Compat: antes se guardaba con JSON.stringify. Intentamos parsear y si falla usamos el valor crudo.
          let value = config.consideraciones;
          try {
            const parsed = JSON.parse(config.consideraciones);
            if (typeof parsed === 'string') value = parsed;
          } catch (e) { /* plain string */ }
          setConsideraciones(value || '');
        }
        setResidente({
          nombre: config.residente_nombre || '',
          contacto: config.residente_contacto || '',
          empresa: config.residente_empresa || 'TUVI'
        });
        if (config.residente_nombre) {
          setBusquedaResidente(config.residente_nombre);
        }
      }
    } catch (err) {
      // Silencioso
    }
  }, []);

  // Cargar planos generados (del editor) para reflejarlos en la Ficha FFE
  const cargarPlanosGen = useCallback(async (cotizacionId) => {
    try {
      const data = await client.query(
        `query($cid: Int!) { planosGenerados(cotizacion_id: $cid) { id tipo_plano hoja titulo aplica imagen_final_url updated_at } }`,
        { cid: cotizacionId }
      );
      setPlanosGen(data.planosGenerados || []);
    } catch (e) { setPlanosGen([]); }
  }, []);

  // La pestaña Planos Generados y el editor (que vive en otra ventana) cambian
  // estos planos. Sin esto, la Ficha FFE seguia mostrando el nombre y la imagen
  // viejos hasta salir y volver a entrar.
  useEffect(() => {
    const cid = cotizacionActual?.id;
    if (!cid) return undefined;
    const refrescar = () => cargarPlanosGen(cid);
    let canal = null;
    if ('BroadcastChannel' in window) {
      canal = new BroadcastChannel('tuvi-planos');
      canal.onmessage = (e) => { if (e.data?.tipo === 'plano-guardado') refrescar(); };
    }
    const onStorage = (e) => { if (e.key === 'tuvi_plano_guardado') refrescar(); };
    // Ultimo respaldo: al volver el foco a la pestaña (p.ej. tras cerrar el editor).
    const onFocus = () => { if (document.visibilityState === 'visible') refrescar(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      if (canal) canal.close();
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, [cotizacionActual?.id, cargarPlanosGen]);

  // Recuadros virtuales a partir de los planos generados (aplicables y con imagen),
  // cada uno con su nombre (tipo + hoja) y la imagen del bucket planos-generados.
  const planosGenRecuadros = planosGen
    .filter(p => p.aplica !== false && p.imagen_final_url)
    .slice()
    .sort((a, b) => ordenTipoPlano(a.tipo_plano) - ordenTipoPlano(b.tipo_plano) || (a.hoja || 0) - (b.hoja || 0))
    .map(p => {
      const base = getTipoPlano(p.tipo_plano)?.nombre || p.tipo_plano;
      const nombre = p.titulo || (((p.hoja || 0) > 0) ? `${base} — Hoja ${(p.hoja || 0) + 1}` : base);
      return { titulo: (nombre || '').toUpperCase(), url: `${UPLOAD_URL}/catalogo/img/planos-generados/${p.imagen_final_url}`, _generado: true };
    });

  // Autoguardado de configuración
  const [autoguardando, setAutoguardando] = useState(false);
  const [ultimoAutoguardado, setUltimoAutoguardado] = useState(null);
  const autoguardadoRef = useRef(null);
  const autoguardadoHabilitado = useRef(false);
  const autoguardadoEnCurso = useRef(false);

  // Guardar configuración
  const [guardandoConfig, setGuardandoConfig] = useState(false);

  const guardarConfig = async () => {
    if (!cotizacionActual) return;
    setGuardandoConfig(true);
    try {
      const gqlR = await client.mutate(`mutation($input: FichaFfeConfigInput!) { guardarFichaFfeConfig(input: $input) { success message } }`, {
        input: {
          cotizacion_id: cotizacionActual.id, source: cotizacionActual.source || 'nueva',
          planos: JSON.stringify(planos), consideraciones: consideraciones || '',
          residente_nombre: residente.nombre, residente_contacto: residente.contacto, residente_empresa: residente.empresa
        }
      });
      if (gqlR.guardarFichaFfeConfig.success) {
        success('Cambios guardados correctamente');
      } else {
        error(gqlR.guardarFichaFfeConfig.message || 'Error al guardar');
      }
    } catch (err) {
      error('Error de conexión al guardar');
    } finally {
      setGuardandoConfig(false);
    }
  };

  // Cargar datos del carrito de compras
  const cargarCarrito = useCallback(async (cotizacionId, source = 'nueva') => {
    try {
      const data = await client.query(`query($cid: Int!, $src: String) { fichaFfeCarrito(cotizacion_id: $cid, source: $src) { pega pega_manual lechadas observaciones_carrito checks } }`, { cid: cotizacionId, src: source });
      const carrito = data.fichaFfeCarrito;
      if (carrito) {
        let lechadasParsed = [{ color: '', ubicacion: '' }, { color: '', ubicacion: '' }, { color: '', ubicacion: '' }, { color: '', ubicacion: '' }];
        let obsParsed = {};
        let checksParsed = { pega: false, lechadas: [], enchapes: {}, actividades: {}, carpinteria: false };
        try { if (carrito.lechadas) lechadasParsed = JSON.parse(carrito.lechadas); } catch(e) {}
        try { if (carrito.observaciones_carrito) obsParsed = JSON.parse(carrito.observaciones_carrito); } catch(e) {}
        try { if (carrito.checks) checksParsed = JSON.parse(carrito.checks); } catch(e) {}
        if (!lechadasParsed || lechadasParsed.length === 0) lechadasParsed = [{ color: '', ubicacion: '' }, { color: '', ubicacion: '' }, { color: '', ubicacion: '' }, { color: '', ubicacion: '' }];
        setCarritoData({
          pega: carrito.pega || 25, pegaManual: carrito.pega_manual || false,
          lechadas: lechadasParsed, observacionesCarrito: obsParsed,
          checks: { pega: checksParsed.pega || false, lechadas: checksParsed.lechadas || [], enchapes: checksParsed.enchapes || {}, actividades: checksParsed.actividades || {}, carpinteria: checksParsed.carpinteria || false }
        });
      }
    } catch (err) {
      // Silencioso
    }
  }, []);

  // Actividades a comprar: las de la cotización con Req. Compra = SI en la matriz,
  // agrupadas por el proveedor de la matriz. Se vuelven a pedir cada vez que se abre
  // la pestaña, para que un cambio en la matriz se vea en todos los carritos.
  const [actividadesCompra, setActividadesCompra] = useState(null);
  useEffect(() => {
    if (tabActiva !== 'carrito' || !cotizacionActual?.id) return;
    let vigente = true;
    setActividadesCompra(null);
    client.query(`query($cid: Int!, $src: String) { carritoActividadesCompra(cotizacion_id: $cid, source: $src) { id codigo descripcion unidad cantidad espacio capitulo proveedor_id proveedor comprado comprado_manual comprado_at comprado_por cantidad_comprada compras_ids } }`,
      { cid: cotizacionActual.id, src: cotizacionActual.source || 'nueva' })
      .then(d => { if (vigente) setActividadesCompra(d.carritoActividadesCompra || []); })
      .catch(() => { if (vigente) setActividadesCompra([]); });
    return () => { vigente = false; };
  }, [tabActiva, cotizacionActual?.id, cotizacionActual?.source]);

  // Guardar datos del carrito
  // Toggle check con guardado inmediato silencioso
  const toggleCheck = (ruta, nuevoValor, nuevoCarritoData) => {
    // nuevoCarritoData ya tiene el estado actualizado
    if (!cotizacionActual) return;
    client.mutate(`mutation($input: FichaFfeCarritoInput!) { guardarFichaFfeCarrito(input: $input) { success } }`, {
      input: { cotizacion_id: cotizacionActual.id, source: cotizacionActual.source || 'nueva',
        pega: nuevoCarritoData.pega, pega_manual: nuevoCarritoData.pegaManual,
        lechadas: JSON.stringify(nuevoCarritoData.lechadas),
        observaciones_carrito: JSON.stringify(nuevoCarritoData.observacionesCarrito),
        checks: JSON.stringify(nuevoCarritoData.checks) }
    }).catch(() => {}); // silencioso
  };

  const guardarCarrito = async () => {
    if (!cotizacionActual) return;
    setGuardandoCarrito(true);
    try {
      const gqlR = await client.mutate(`mutation($input: FichaFfeCarritoInput!) { guardarFichaFfeCarrito(input: $input) { success message } }`, {
        input: { cotizacion_id: cotizacionActual.id, source: cotizacionActual.source || 'nueva',
          pega: carritoData.pega, pega_manual: carritoData.pegaManual,
          lechadas: JSON.stringify(carritoData.lechadas),
          observaciones_carrito: JSON.stringify(carritoData.observacionesCarrito),
          checks: JSON.stringify(carritoData.checks) }
      });
      if (gqlR.guardarFichaFfeCarrito.success) {
        success('Carrito guardado');
      } else {
        error(gqlR.guardarFichaFfeCarrito.message || 'Error al guardar carrito');
      }
    } catch (err) {
      error('Error de conexión');
    } finally {
      setGuardandoCarrito(false);
    }
  };

  // Autoguardado silencioso de configuración + carrito
  const autoguardar = useCallback(async () => {
    if (autoguardadoEnCurso.current || !cotizacionActual) return;
    autoguardadoEnCurso.current = true;
    setAutoguardando(true);
    try {
      // Guardar config
      const src = cotizacionActual.source || 'nueva';
      const gqlConfig = await client.mutate(`mutation($input: FichaFfeConfigInput!) { guardarFichaFfeConfig(input: $input) { success } }`, {
        input: { cotizacion_id: cotizacionActual.id, source: src,
          planos: JSON.stringify(planos), consideraciones: consideraciones || '',
          residente_nombre: residente.nombre, residente_contacto: residente.contacto, residente_empresa: residente.empresa }
      });

      // Guardar carrito
      await client.mutate(`mutation($input: FichaFfeCarritoInput!) { guardarFichaFfeCarrito(input: $input) { success } }`, {
        input: { cotizacion_id: cotizacionActual.id, source: src,
          pega: carritoData.pega, pega_manual: carritoData.pegaManual,
          lechadas: JSON.stringify(carritoData.lechadas),
          observaciones_carrito: JSON.stringify(carritoData.observacionesCarrito),
          checks: JSON.stringify(carritoData.checks) }
      });

      if (gqlConfig.guardarFichaFfeConfig.success) {
        setUltimoAutoguardado(new Date());
      }
    } catch (err) {
      // Silencioso
    } finally {
      autoguardadoEnCurso.current = false;
      setAutoguardando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cotizacionActual, planos, consideraciones, residente, carritoData]);

  // Disparar autoguardado con debounce cuando cambian datos de config
  useEffect(() => {
    if (vista !== 'detalle') return;
    if (!autoguardadoHabilitado.current) return;

    if (autoguardadoRef.current) clearTimeout(autoguardadoRef.current);
    autoguardadoRef.current = setTimeout(() => {
      autoguardar();
    }, 3000);

    return () => {
      if (autoguardadoRef.current) clearTimeout(autoguardadoRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planos, consideraciones, residente, carritoData]);

  // Habilitar autoguardado con delay tras entrar a detalle
  useEffect(() => {
    if (vista === 'detalle') {
      autoguardadoHabilitado.current = false;
      const timer = setTimeout(() => {
        autoguardadoHabilitado.current = true;
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      autoguardadoHabilitado.current = false;
      setUltimoAutoguardado(null);
    }
  }, [vista]);

  // Calcular valores del carrito de compras (usa formulas de BD con fallback hardcoded)
  const calcularCarrito = useCallback(() => {
    if (!cotizacionActual) return [];

    // Coeficientes: BD → fallback hardcoded
    const F = (clave, fallback) => (formulasDB && formulasDB[clave] !== undefined) ? formulasDB[clave] : fallback;
    const despPiso = F('desperdicio_piso', 1.1);
    const fZocalo = F('factor_zocalo', 0.03);
    const despPisoBano = F('desperdicio_piso_bano', 1.1);
    const profZH = F('profundidad_zona_humeda', 0.9);
    const despDucha = F('desperdicio_paredes_ducha', 1.1);
    const despSeca = F('desperdicio_paredes_seca', 1.05);
    const anchoPuerta = F('ancho_puerta_bano', 0.7);
    const despBalcon = F('desperdicio_balcon', 1.1);

    const area = parseFloat(cotizacionActual.area_m2) || 0;
    const socialAlto = parseFloat(cotizacionActual.bano_social_alto) || 0;
    const socialAncho = parseFloat(cotizacionActual.bano_social_ancho) || 0;
    const ppalAlto = parseFloat(cotizacionActual.bano_ppal_alto) || 0;
    const ppalAncho = parseFloat(cotizacionActual.bano_ppal_ancho) || 0;
    const balconVal = parseFloat(cotizacionActual.balcon) || 0;
    const alturaDucha = parseFloat(cotizacionActual.altura_ducha) || 0;

    const areaSocial = socialAlto * socialAncho;
    const areaPpal = ppalAlto * ppalAncho;

    const pisoGeneral = (area - areaSocial - areaPpal - balconVal) * despPiso;
    const zocalo = (area - areaSocial - areaPpal - balconVal) * fZocalo;
    const pisoPpalZH = ppalAncho * profZH * despPisoBano;
    const pisoPpalZS = (areaPpal * despPisoBano) - pisoPpalZH;
    const pisoSocialZH = socialAncho * profZH * despPisoBano;
    const pisoSocialZS = (areaSocial * despPisoBano) - pisoSocialZH;
    const paredesDuchaPpal = (ppalAncho + profZH + profZH) * alturaDucha * despDucha;
    const paredesPpalZS = (ppalAncho + ((ppalAlto - profZH) * 2) - anchoPuerta) * alturaDucha * despSeca;
    const paredesSocialZS = (socialAncho + ((socialAlto - profZH) * 2) - anchoPuerta) * alturaDucha * despSeca;
    const paredesDuchaSocial = (socialAncho + profZH + profZH) * alturaDucha * despDucha;
    const pisoBalcon = balconVal * despBalcon;

    return [
      { nombre: 'PISO GENERAL', valor: pisoGeneral, unidad: 'M2', nota: '' },
      { nombre: 'ZOCALO', valor: zocalo, unidad: 'M2', nota: '' },
      { nombre: 'PISO BAÑO PPAL Z.H', valor: pisoPpalZH, unidad: 'M2', nota: 'Zona Humeda' },
      { nombre: 'PISO BAÑO PPAL Z.S', valor: pisoPpalZS, unidad: 'M2', nota: 'Zona Seca' },
      { nombre: 'PISO BAÑO SOCIAL Z.H', valor: pisoSocialZH, unidad: 'M2', nota: 'Zona Humeda' },
      { nombre: 'PISO BAÑO SOCIAL Z.S', valor: pisoSocialZS, unidad: 'M2', nota: 'Zona Seca' },
      { nombre: 'PAREDES DUCHA PPAL', valor: paredesDuchaPpal, unidad: 'M2', nota: '' },
      { nombre: 'PAREDES BAÑO PPAL Z.S', valor: paredesPpalZS, unidad: 'M2', nota: '' },
      { nombre: 'PAREDES BAÑO SOCIAL Z.S', valor: paredesSocialZS, unidad: 'M2', nota: '' },
      { nombre: 'PAREDES DUCHA SOCIAL', valor: paredesDuchaSocial, unidad: 'M2', nota: '' },
      { nombre: 'PISO BALCON', valor: pisoBalcon, unidad: 'M2', nota: '' }
    ];
  }, [cotizacionActual, formulasDB]);

  // Ver detalle de cotización
  const verDetalle = (cotizacion, soloLectura = false) => {
    setModoLectura(soloLectura);
    setVista('detalle');
    // Seleccionar primera pestaña disponible según permisos
    const tabDefault = permisoTabFicha ? 'ficha' : permisoTabContratistas ? 'contratistas' : permisoTabCarrito ? 'carrito' : 'ficha';
    setTabActiva(tabDefault);
    cargarActividades(cotizacion.id, cotizacion.source || 'nueva');
    cargarConfig(cotizacion.id, cotizacion.source || 'nueva');
    cargarCarrito(cotizacion.id, cotizacion.source || 'nueva');
    cargarPlanosGen(cotizacion.id);
  };

  // Toggle de botones FFE (llaves, pago, firma)
  const toggleFFE = async (cotizacionId, source, campo) => {
    // Verificar si al activar este campo se completarían los 3
    const cot = cotizaciones.find(c => c.id === cotizacionId && c.source === source);
    if (cot) {
      const valores = {
        ffe_llaves: parseInt(cot.ffe_llaves) || 0,
        ffe_pago: parseInt(cot.ffe_pago) || 0,
        ffe_firma: parseInt(cot.ffe_firma) || 0
      };
      // Si el campo actual está en 0 (se va a activar), simular el toggle
      const nuevoValor = valores[campo] === 0 ? 1 : 0;
      valores[campo] = nuevoValor;
      const todosActivos = valores.ffe_llaves && valores.ffe_pago && valores.ffe_firma;

      if (todosActivos) {
        // Mostrar modal de confirmación antes de enviar
        setModalEnvioProyecto({ cotizacionId, source, campo });
        return;
      }
    }

    await ejecutarToggleFFE(cotizacionId, source, campo);
  };

  const ejecutarToggleFFE = async (cotizacionId, source, campo) => {
    try {
      const gqlR = await client.mutate(`mutation($cid: Int!, $src: String!, $campo: String!) { toggleFfe(cotizacion_id: $cid, source: $src, campo: $campo) { success message ffe_llaves ffe_pago ffe_firma ffe_enviado_proyecto todos_activos proyecto_id } }`, { cid: cotizacionId, src: source, campo });
      const data = gqlR.toggleFfe;
      if (data.success) {
        setCotizaciones(prev => prev.map(c =>
          c.id === cotizacionId && c.source === source
            ? { ...c, ffe_llaves: data.ffe_llaves, ffe_pago: data.ffe_pago, ffe_firma: data.ffe_firma, ffe_enviado_proyecto: data.ffe_enviado_proyecto }
            : c
        ));
        if (data.todos_activos && data.proyecto_id) {
          success(`Proyecto creado exitosamente (ID: ${data.proyecto_id})`);
        }
      } else {
        error(data.message || 'Error al actualizar');
      }
    } catch (err) {
      error('Error de conexión');
    }
  };

  // Actualizar avance FFE
  const actualizarAvance = async (cotizacionId, source, avance) => {
    try {
      const gqlR = await client.mutate(`mutation($cid: Int!, $src: String!, $avance: String!) { actualizarAvanceFfe(cotizacion_id: $cid, source: $src, avance_ffe: $avance) { success message avance_ffe } }`, { cid: cotizacionId, src: source, avance: avance });
      const data = gqlR.actualizarAvanceFfe;
      if (data.success) {
        // Si se selecciona "firmado_aprobado", activar automáticamente el check de firma
        if (avance === 'firmado_aprobado') {
          const cot = cotizaciones.find(c => c.id === cotizacionId && c.source === source);
          if (cot && !parseInt(cot.ffe_firma)) {
            await toggleFFE(cotizacionId, source, 'ffe_firma');
          }
        }
        setCotizaciones(prev => prev.map(c =>
          c.id === cotizacionId && c.source === source
            ? { ...c, avance_ffe: data.avance_ffe }
            : c
        ));
      } else {
        error(data.message || 'Error al actualizar avance');
      }
    } catch (err) {
      error('Error de conexión');
    }
  };

  // Volver a lista
  const volver = () => {
    setVista('lista');
    setModoLectura(false);
    setCotizacionActual(null);
    setActividades([]);
    setEditandoActividad(null);
    // Reset planos, consideraciones, residente
    setPlanos([
      { titulo: 'PLANO GENERALIDADES', urls: [''] },
      { titulo: 'PLANO MUROS NUEVOS Y DEMOLICIONES', urls: [''] }
    ]);
    setConsideraciones('');
    setResidente({ nombre: '', contacto: '', empresa: 'TUVI' });
    setBusquedaResidente('');
    setCarritoData({
      pega: 25,
      pegaManual: false,
      lechadas: [
        { color: '', ubicacion: '' },
        { color: '', ubicacion: '' },
        { color: '', ubicacion: '' },
        { color: '', ubicacion: '' }
      ],
      observacionesCarrito: {},
      checks: { pega: false, lechadas: [], enchapes: {}, actividades: {}, carpinteria: false }
    });
  };

  // Iniciar edición de imagen
  const iniciarEdicion = (actividad) => {
    setEditandoActividad(actividad.actividad_id);
    setFormImagen({
      nombre: actividad.imagen_nombre || '',
      codigo: actividad.imagen_codigo || '',
      url_imagen: actividad.url_imagen || ''
    });
  };

  // Cancelar edición
  const cancelarEdicion = () => {
    setEditandoActividad(null);
    setFormImagen({ nombre: '', codigo: '', url_imagen: '' });
  };

  // Guardar imagen (solo nombre, código, URL)
  const guardarImagen = async (actividadId) => {
    if (!formImagen.nombre && !formImagen.codigo && !formImagen.url_imagen) {
      error('Ingrese al menos un campo (nombre, código o URL)');
      return;
    }

    setGuardando(true);
    try {
      const act = actividades.find(a => a.actividad_id === actividadId);
      const gqlR = await client.mutate(`mutation($cid: Int!, $aid: Int!, $src: String!, $nombre: String, $codigo: String, $url: String, $desc: String, $obs: String, $prov: String) { guardarImagenFFE(cotizacion_id: $cid, actividad_id: $aid, source: $src, nombre: $nombre, codigo: $codigo, url_imagen: $url, descripcion_custom: $desc, observaciones: $obs, proveedor: $prov) { success message } }`, {
        cid: cotizacionActual.id, aid: actividadId, src: cotizacionActual.source || 'nueva',
        nombre: formImagen.nombre, codigo: formImagen.codigo, url: formImagen.url_imagen,
        desc: act?.descripcion_custom || '', obs: act?.observaciones || '', prov: act?.proveedor || '',
      });
      const data = gqlR.guardarImagenFFE;
      if (data.success) {
        success('Imagen guardada');
        // Actualización optimista local
        setActividades(prev => prev.map(a =>
          a.actividad_id === actividadId ? {
            ...a,
            imagen_nombre: formImagen.nombre,
            imagen_codigo: formImagen.codigo,
            url_imagen: formImagen.url_imagen
          } : a
        ));
        setEditandoActividad(null);
      } else {
        error(data.message || 'Error al guardar');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  // Edición inline de campo (descripcion, observaciones, proveedor)
  const iniciarEdicionCampo = (actividadId, campo, valorActual) => {
    setEditandoCampo({ id: actividadId, campo });
    setCampoTemp(valorActual || '');
  };

  const cancelarEdicionCampo = () => {
    setEditandoCampo(null);
    setCampoTemp('');
  };

  // Seleccionar un enchape del modal: guarda observaciones + proveedor en una sola llamada
  const seleccionarEnchape = async (enchape, actividadId) => {
    const texto = `${enchape.ref} - ${enchape.color} - ${enchape.formato}`.replace(/ - $/g, '').replace(/^ - /g, '');
    const nuevoProveedor = enchape.proveedor_nombre || '';

    const act = actividades.find(a => a.actividad_id === actividadId);
    if (!act) { setModalEnchape(null); setBusquedaEnchape(''); return; }

    const obsOriginal = act.observaciones || '';
    const provOriginal = act.proveedor || '';

    // Actualización optimista local (ambos campos a la vez)
    setActividades(prev => prev.map(a =>
      a.actividad_id === actividadId
        ? { ...a, observaciones: texto, proveedor: nuevoProveedor || a.proveedor }
        : a
    ));
    setModalEnchape(null);
    setBusquedaEnchape('');

    try {
      const gqlR = await client.mutate(
        `mutation($cid: Int!, $aid: Int!, $src: String!, $nombre: String, $codigo: String, $url: String, $desc: String, $obs: String, $prov: String) {
           guardarImagenFFE(cotizacion_id: $cid, actividad_id: $aid, source: $src, nombre: $nombre, codigo: $codigo, url_imagen: $url, descripcion_custom: $desc, observaciones: $obs, proveedor: $prov) { success message }
         }`,
        {
          cid: cotizacionActual.id,
          aid: actividadId,
          src: cotizacionActual.source || 'nueva',
          nombre: act.imagen_nombre || '',
          codigo: act.imagen_codigo || '',
          url: act.url_imagen || '',
          desc: act.descripcion_custom || '',
          obs: texto,
          prov: nuevoProveedor || provOriginal,
        }
      );
      if (!gqlR.guardarImagenFFE.success) {
        // Revertir si falló
        setActividades(prev => prev.map(a =>
          a.actividad_id === actividadId
            ? { ...a, observaciones: obsOriginal, proveedor: provOriginal }
            : a
        ));
        error(gqlR.guardarImagenFFE.message || 'Error al guardar enchape');
      }
    } catch (err) {
      setActividades(prev => prev.map(a =>
        a.actividad_id === actividadId
          ? { ...a, observaciones: obsOriginal, proveedor: provOriginal }
          : a
      ));
      error('Error al guardar enchape');
    }
  };

  const guardarCampoInline = async (actividadId, campo, valor) => {
    const act = actividades.find(a => a.actividad_id === actividadId);
    if (!act) return;

    // Si el valor no cambió, solo cerrar
    const valorOriginal = act[campo] || '';
    if (valor === valorOriginal) {
      cancelarEdicionCampo();
      return;
    }

    // Actualización optimista local
    setActividades(prev => prev.map(a =>
      a.actividad_id === actividadId ? { ...a, [campo]: valor } : a
    ));
    cancelarEdicionCampo();

    try {
      const body = {
        cid: cotizacionActual.id, aid: actividadId, src: cotizacionActual.source || 'nueva',
        nombre: act.imagen_nombre || '', codigo: act.imagen_codigo || '',
        url: act.url_imagen || '', desc: act.descripcion_custom || '',
        obs: act.observaciones || '', prov: act.proveedor || '',
      };
      // Update the specific field
      const fieldMap = { descripcion_custom: 'desc', observaciones: 'obs', proveedor: 'prov', imagen_nombre: 'nombre', imagen_codigo: 'codigo', url_imagen: 'url' };
      if (fieldMap[campo]) body[fieldMap[campo]] = valor;

      const gqlR = await client.mutate(`mutation($cid: Int!, $aid: Int!, $src: String!, $nombre: String, $codigo: String, $url: String, $desc: String, $obs: String, $prov: String) { guardarImagenFFE(cotizacion_id: $cid, actividad_id: $aid, source: $src, nombre: $nombre, codigo: $codigo, url_imagen: $url, descripcion_custom: $desc, observaciones: $obs, proveedor: $prov) { success message } }`, body);
      if (!gqlR.guardarImagenFFE.success) {
        // Revertir si falló
        setActividades(prev => prev.map(a =>
          a.actividad_id === actividadId ? { ...a, [campo]: valorOriginal } : a
        ));
        error(gqlR.guardarImagenFFE.message || 'Error al guardar');
      }
    } catch (err) {
      console.error('Error:', err);
      setActividades(prev => prev.map(a =>
        a.actividad_id === actividadId ? { ...a, [campo]: valorOriginal } : a
      ));
      error('Error de conexión al guardar');
    }
  };

  // Eliminar imagen
  const eliminarImagen = async (actividadId) => {
    if (!window.confirm('¿Eliminar la imagen de esta actividad?')) return;

    try {
      const gqlR = await client.mutate(`mutation($cid: Int!, $aid: Int!, $src: String!) { eliminarImagenFFE(cotizacion_id: $cid, actividad_id: $aid, source: $src) { success message } }`, {
        cid: cotizacionActual.id, aid: actividadId, src: cotizacionActual.source || 'nueva',
      });
      const data = gqlR.eliminarImagenFFE;
      if (data.success) {
        success('Imagen eliminada');
        // Actualización optimista local
        setActividades(prev => prev.map(a =>
          a.actividad_id === actividadId ? {
            ...a,
            imagen_nombre: null,
            imagen_codigo: null,
            url_imagen: null
          } : a
        ));
      } else {
        error(data.message || 'Error al eliminar');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error de conexión al eliminar');
    }
  };

  // Iniciar edición de cantidad
  const iniciarEdicionCantidad = (actividad) => {
    setEditandoCantidad(actividad.actividad_id);
    setCantidadTemp(String(actividad.cantidad));
  };

  // Guardar cantidad
  const guardarCantidad = async (actividadId) => {
    const nuevaCantidad = parseFloat(cantidadTemp);
    if (!nuevaCantidad || nuevaCantidad <= 0) {
      error('La cantidad debe ser mayor a 0');
      return;
    }

    try {
      const gqlR = await client.mutate(`mutation($cid: Int!, $aid: Int!, $src: String!, $cant: Float!) { actualizarCantidadFFE(cotizacion_id: $cid, actividad_id: $aid, source: $src, cantidad: $cant) { success message } }`, {
        cid: cotizacionActual.id, aid: actividadId, src: cotizacionActual.source || 'nueva', cant: nuevaCantidad,
      });
      const data = gqlR.actualizarCantidadFFE;
      if (data.success) {
        success('Cantidad actualizada');
        // Actualización optimista local
        setActividades(prev => prev.map(a =>
          a.actividad_id === actividadId ? { ...a, cantidad: nuevaCantidad } : a
        ));
        setEditandoCantidad(null);
      } else {
        error(data.message || 'Error al actualizar');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error de conexión al actualizar cantidad');
    }
  };

  // Editar descripción de actividad (sincroniza con cotización original)
  const iniciarEdicionDescAct = (act) => {
    setEditandoDescAct(act.actividad_id);
    setDescActTemp(act.descripcion || '');
  };
  const cancelarEdicionDescAct = () => {
    setEditandoDescAct(null);
    setDescActTemp('');
  };
  const guardarDescripcionAct = async (actividadId) => {
    const nuevaDesc = (descActTemp || '').trim();
    if (!nuevaDesc) {
      error('La descripción no puede estar vacía');
      return;
    }
    try {
      const gqlR = await client.mutate(
        `mutation($cid: Int!, $aid: Int!, $src: String!, $desc: String!) { actualizarDescripcionFFE(cotizacion_id: $cid, actividad_id: $aid, source: $src, descripcion: $desc) { success message } }`,
        { cid: cotizacionActual.id, aid: actividadId, src: cotizacionActual.source || 'nueva', desc: nuevaDesc }
      );
      const data = gqlR.actualizarDescripcionFFE;
      if (data.success) {
        success('Descripción actualizada en cotización');
        setActividades(prev => prev.map(a =>
          a.actividad_id === actividadId ? { ...a, descripcion: nuevaDesc } : a
        ));
        setEditandoDescAct(null);
        setDescActTemp('');
      } else {
        error(data.message || 'Error al actualizar descripción');
      }
    } catch (err) {
      console.error('Error:', err);
      error('Error de conexión al actualizar descripción');
    }
  };

  // Cancelar edición de cantidad
  const cancelarEdicionCantidad = () => {
    setEditandoCantidad(null);
    setCantidadTemp('');
  };

  // URL del proxy para Google Drive
  const getProxyUrl = (url) => {
    if (!url) return null;
    // Si es URL del API/MinIO (catalogo), usar directamente sin proxy
    if (url.includes('/upload/catalogo/') || url.includes('api.tuvisistema.com')) return url;
    return `${BACKEND_URL}/api/documentos/proxy-imagen.php?url=${encodeURIComponent(url)}`;
  };

  // Orden definido de espacios
  const ORDEN_ESPACIOS = ['Generales', 'Cocina', 'Habitaciones', 'Estudio', 'Baños', 'Zona de Ropas', 'Balcón / Terraza', 'Finales'];

  // Agrupar actividades por espacio y ordenar
  const actividadesPorEspacio = (() => {
    const grupos = actividades.reduce((acc, act) => {
      const espacio = act.espacio || 'SIN ESPACIO';
      if (!acc[espacio]) acc[espacio] = [];
      acc[espacio].push(act);
      return acc;
    }, {});
    return Object.entries(grupos).sort(([a], [b]) => {
      const idxA = ORDEN_ESPACIOS.findIndex(e => e.toLowerCase() === a.toLowerCase());
      const idxB = ORDEN_ESPACIOS.findIndex(e => e.toLowerCase() === b.toLowerCase());
      return (idxA >= 0 ? idxA : ORDEN_ESPACIOS.length) - (idxB >= 0 ? idxB : ORDEN_ESPACIOS.length);
    });
  })();

  // ==============================
  // RENDER LISTA DE COTIZACIONES
  // ==============================
  const renderLista = () => {
    // Filtrar por tab
    const cotizacionesFiltradas = cotizaciones.filter(cot => {
      if (tabLista === 'exportados') return parseInt(cot.ffe_enviado_proyecto) === 1;
      return parseInt(cot.ffe_enviado_proyecto) !== 1;
    });

    // Filtro por ZONA: es la base del tablero, por eso va antes que todo lo
    // demas. Los 3 indicadores se calculan sobre este conjunto.
    const cotizacionesPorZona = filtroZona
      ? cotizacionesFiltradas.filter(cot =>
          filtroZona === '__sin__'
            ? !(cot.zona || '').trim()
            : (cot.zona || '').trim() === filtroZona)
      : cotizacionesFiltradas;

    // Los 3 estados del tablero, con el desglose de llaves y pago de cada uno.
    const ESTADOS_TABLERO = [
      { clave: 'firmado_aprobado', titulo: 'Firmado y Aprobado', tono: 'verde' },
      { clave: 'en_revision', titulo: 'En revisión del cliente', tono: 'azul' },
      { clave: 'en_elaboracion', titulo: 'En elaboración', tono: 'ambar' },
    ];
    const indicadores = ESTADOS_TABLERO.map(e => {
      const delEstado = cotizacionesPorZona.filter(c => (c.avance_ffe || 'por_agendar') === e.clave);
      return {
        ...e,
        total: delEstado.length,
        llaves: delEstado.filter(c => parseInt(c.ffe_llaves) === 1).length,
        pago: delEstado.filter(c => parseInt(c.ffe_pago) === 1).length,
      };
    });

    // Filtro por el indicador seleccionado
    const cotizacionesPorAvance = filtroAvance
      ? cotizacionesPorZona.filter(c => (c.avance_ffe || 'por_agendar') === filtroAvance)
      : cotizacionesPorZona;

    // Filtro por comercial (desde el menú del encabezado de la columna)
    const cotizacionesPorComercial = filtroComercial
      ? cotizacionesPorAvance.filter(cot =>
          filtroComercial === '__sin__'
            ? !(cot.comercial || '').trim()
            : (cot.comercial || '').trim() === filtroComercial)
      : cotizacionesPorAvance;

    // Comerciales presentes en la pestaña actual (para el menú de filtro)
    const comercialesDisponibles = [...new Set(
      cotizacionesFiltradas.map(c => (c.comercial || '').trim()).filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'es'));
    const hayCotizacionesSinComercial = cotizacionesFiltradas.some(c => !(c.comercial || '').trim());

    // Filtrar por búsqueda (flexible: ignora tildes, mayúsculas, espacios)
    const cotizacionesBusqueda = busquedaFFE.trim()
      ? cotizacionesPorComercial.filter(cot => {
          if (String(cot.consecutivo || '').includes(busquedaFFE.trim())) return true;
          return matchesAnyField(cot, ['nombre_cliente', 'proyecto_nombre', 'apartamento', 'comercial'], busquedaFFE);
        })
      : cotizacionesPorComercial;

    const countPendientes = cotizaciones.filter(c => parseInt(c.ffe_enviado_proyecto) !== 1).length;
    const countExportados = cotizaciones.filter(c => parseInt(c.ffe_enviado_proyecto) === 1).length;

    return (
      <>
        <div className="ffe-page-header">
          <div className="ffe-header-info">
            <h1>Ficha FFE</h1>
            <p>{cotizaciones.length} cotizaciones firmadas</p>
          </div>
          <button className="ffe-btn-exportar" onClick={exportarExcelFFE}>
            📊 Exportar Excel
          </button>
        </div>

        {/* Tablero: 3 indicadores por estado de avance, filtrables por zona.
            Cada tarjeta funciona como filtro de la tabla al hacerle clic. */}
        <div className="ffe-tablero">
          <div className="ffe-tablero-zona">
            <label htmlFor="ffe-filtro-zona">Zona</label>
            <select
              id="ffe-filtro-zona"
              value={filtroZona}
              onChange={(e) => setFiltroZona(e.target.value)}
            >
              <option value="">Todas las zonas</option>
              {zonasFFE.map(z => <option key={z.id} value={z.nombre}>{z.nombre}</option>)}
              <option value="__sin__">Sin zona asignada</option>
            </select>
            {(filtroZona || filtroAvance) && (
              <button
                className="ffe-tablero-limpiar"
                onClick={() => { setFiltroZona(''); setFiltroAvance(null); }}
              >
                Limpiar filtros
              </button>
            )}
          </div>

          <div className="ffe-tablero-cards">
            {indicadores.map(ind => {
              const activa = filtroAvance === ind.clave;
              return (
                <button
                  key={ind.clave}
                  type="button"
                  className={`ffe-card ffe-card-${ind.tono}${activa ? ' ffe-card-activa' : ''}`}
                  onClick={() => setFiltroAvance(activa ? null : ind.clave)}
                  title={activa ? 'Quitar este filtro' : `Ver solo las de "${ind.titulo}"`}
                >
                  <span className="ffe-card-titulo">{ind.titulo}</span>
                  <span className="ffe-card-total">{ind.total}</span>
                  <span className="ffe-card-sub">
                    <span className="ffe-card-chip">
                      <span className="ffe-chip-icono">🔑</span>
                      <span className="ffe-chip-num">{ind.llaves}</span>
                      <span className="ffe-chip-txt">con llaves</span>
                    </span>
                    <span className="ffe-card-chip">
                      <span className="ffe-chip-icono">💲</span>
                      <span className="ffe-chip-num">{ind.pago}</span>
                      <span className="ffe-chip-txt">con pago</span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="ffe-lista-tabs">
          <button
            className={`ffe-lista-tab ${tabLista === 'pendientes' ? 'active' : ''}`}
            onClick={() => { setTabLista('pendientes'); setBusquedaFFE(''); }}
          >
            Pendientes <span className="ffe-tab-count">{countPendientes}</span>
          </button>
          <button
            className={`ffe-lista-tab ${tabLista === 'exportados' ? 'active' : ''}`}
            onClick={() => { setTabLista('exportados'); setBusquedaFFE(''); }}
          >
            Exportados a Proyectos <span className="ffe-tab-count">{countExportados}</span>
          </button>
          <div className="ffe-lista-search">
            <input
              type="text"
              placeholder="Buscar por cliente, proyecto, apto o consecutivo..."
              value={busquedaFFE}
              onChange={(e) => setBusquedaFFE(e.target.value)}
              className="ffe-search-input"
            />
            {busquedaFFE && (
              <button className="ffe-search-clear" onClick={() => setBusquedaFFE('')}>x</button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="ffe-loading">Cargando cotizaciones...</div>
        ) : cotizacionesBusqueda.length === 0 ? (
          <div className="ffe-empty">
            {busquedaFFE ? 'No se encontraron resultados' : tabLista === 'exportados' ? 'No hay fichas exportadas a proyectos' : 'No hay cotizaciones pendientes'}
          </div>
        ) : (
          <div className="ffe-table-container">
            <table className="ffe-table">
              <thead>
                <tr>
                  <th>Consecutivo</th>
                  <th>Cliente</th>
                  <th>Proyecto</th>
                  <th>Apto</th>
                  <th>Fecha</th>
                  <th>Valor Final</th>
                  <th className="ffe-th-comercial">
                    <button
                      type="button"
                      className={`ffe-th-filtro ${filtroComercial ? 'activo' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setMenuComercialAbierto(v => !v); }}
                      title="Filtrar por comercial"
                    >
                      Comercial <span className="ffe-th-caret">▾</span>
                    </button>
                    {menuComercialAbierto && (
                      <div className="ffe-comercial-menu" onClick={(e) => e.stopPropagation()}>
                        <button className={`ffe-cm-item ${!filtroComercial ? 'sel' : ''}`} onClick={() => { setFiltroComercial(''); setMenuComercialAbierto(false); }}>
                          Todos los comerciales
                        </button>
                        {comercialesDisponibles.map(nom => (
                          <button key={nom} className={`ffe-cm-item ${filtroComercial === nom ? 'sel' : ''}`} onClick={() => { setFiltroComercial(nom); setMenuComercialAbierto(false); }}>
                            {nom}
                          </button>
                        ))}
                        {hayCotizacionesSinComercial && (
                          <button className={`ffe-cm-item ffe-cm-sin ${filtroComercial === '__sin__' ? 'sel' : ''}`} onClick={() => { setFiltroComercial('__sin__'); setMenuComercialAbierto(false); }}>
                            Sin comercial
                          </button>
                        )}
                      </div>
                    )}
                  </th>
                  <th>Act. FFE</th>
                  {tabLista !== 'exportados' && <th>Avance</th>}
                  {tabLista !== 'exportados' && <th>F. Inicio Prob.</th>}
                  {tabLista === 'exportados' && <th>Compra</th>}
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cotizacionesBusqueda.map(cot => (
                  <tr key={`${cot.source}-${cot.id}`}>
                    <td className="ffe-consecutivo">COT-{String(cot.consecutivo).padStart(4, '0')}{cot.source === 'vieja' ? <span className="ffe-badge-vieja" title="Cotización importada"> (V)</span> : ''}</td>
                    <td>{cot.nombre_cliente}</td>
                    <td>{cot.proyecto_nombre}</td>
                    <td>{cot.apartamento}</td>
                    <td>{cot.fecha}</td>
                    <td className="ffe-valor">{formatCurrency(cot.valor_mostrar)}</td>
                    <td style={{ fontSize: '0.78rem', color: '#475569' }} onClick={(e) => e.stopPropagation()}>
                      {permisoEditarComercial ? (
                        editandoComercialId === `${cot.source}-${cot.id}` ? (
                          <select
                            className="ffe-select-avance"
                            value={cot.comercial || ''}
                            autoFocus
                            onBlur={() => setEditandoComercialId(null)}
                            onChange={(e) => guardarComercialFFE(cot.id, cot.source, e.target.value)}
                          >
                            <option value="">-- Sin comercial --</option>
                            {listaComerciales.map(c => (
                              <option key={c.id} value={c.nombre}>{c.nombre}</option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className="ffe-campo-editable"
                            onClick={() => setEditandoComercialId(`${cot.source}-${cot.id}`)}
                            title="Clic para cambiar comercial"
                            style={{ cursor: 'pointer' }}
                          >
                            {cot.comercial || '-'}
                          </span>
                        )
                      ) : (cot.comercial || '-')}
                    </td>
                    <td className="ffe-count">{cot.total_ffe}</td>
                    {tabLista !== 'exportados' && (
                    <td>
                      <select
                        className={`ffe-select-avance avance-${cot.avance_ffe || 'por_agendar'}`}
                        value={cot.avance_ffe || 'por_agendar'}
                        onChange={(e) => actualizarAvance(cot.id, cot.source, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="por_agendar">Por Agendar</option>
                        <option value="agendado">Agendado</option>
                        <option value="en_elaboracion">En elaboración</option>
                        <option value="en_revision">En revisión del cliente</option>
                        <option value="firmado_aprobado">Firmado y Aprobado</option>
                      </select>
                    </td>
                    )}
                    {tabLista !== 'exportados' && (
                      <td>
                        <input type="date" className="ffe-date-input" value={cot.fecha_probable_inicio || ''}
                          onClick={e => e.stopPropagation()}
                          onChange={async (e) => {
                            try {
                              await client.mutate(`mutation($id: Int!, $s: String!, $f: String!) { actualizarFechaProbableInicio(id: $id, source: $s, fecha: $f) { success } }`,
                                { id: cot.id, s: cot.source || 'nueva', f: e.target.value });
                              cargarCotizaciones();
                            } catch {}
                          }} />
                      </td>
                    )}
                    {tabLista === 'exportados' && (
                      <td onClick={e => e.stopPropagation()}>
                        <label className={`ffe-compra-toggle ${cot.estado_compra === 'comprado' ? 'comprado' : ''}`}>
                          <input type="checkbox" checked={cot.estado_compra === 'comprado'}
                            onChange={async () => {
                              const nuevoEstado = cot.estado_compra === 'comprado' ? 'p_compra' : 'comprado';
                              try {
                                await client.mutate(`mutation($id: Int!, $s: String!, $e: String!) { cambiarEstadoCompra(id: $id, source: $s, estado_compra: $e) { success } }`,
                                  { id: cot.id, s: cot.source || 'nueva', e: nuevoEstado });
                                cargarCotizaciones();
                              } catch {}
                            }} />
                          <span>{cot.estado_compra === 'comprado' ? 'Comprado' : 'P. Compra'}</span>
                        </label>
                      </td>
                    )}
                    <td>
                      {parseInt(cot.ffe_enviado_proyecto) ? (
                        <span className="ffe-badge-enviado">Enviado a Proyectos</span>
                      ) : (
                        <div className="ffe-toggles">
                          <button
                            className={`ffe-toggle-btn ${parseInt(cot.ffe_llaves) ? 'active' : ''}`}
                            onClick={() => toggleFFE(cot.id, cot.source, 'ffe_llaves')}
                            title="Llaves"
                          >
                            🔑
                          </button>
                          <button
                            className={`ffe-toggle-btn ${parseInt(cot.ffe_pago) ? 'active' : ''}`}
                            onClick={() => toggleFFE(cot.id, cot.source, 'ffe_pago')}
                            title="Pago"
                          >
                            💲
                          </button>
                          <button
                            className={`ffe-toggle-btn ${parseInt(cot.ffe_firma) ? 'active' : ''}`}
                            onClick={() => toggleFFE(cot.id, cot.source, 'ffe_firma')}
                            title="Firma"
                          >
                            ✍️
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      {parseInt(cot.ffe_enviado_proyecto) ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="btn-ver-detalle"
                          onClick={() => verDetalle(cot, true)}
                          title="Ver detalle del proyecto exportado"
                        >
                          Ver Detalle
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`¿Devolver "${cot.proyecto_nombre} ${cot.apartamento || ''}" a pendientes?\nPodrá editarla y luego sincronizar los cambios al proyecto.`)) return;
                            try {
                              const r = await client.mutate(`mutation($cid: Int!, $src: String!) { devolverFFEaPendientes(cotizacion_id: $cid, source: $src) { success message } }`, { cid: cot.id, src: cot.source || 'nueva' });
                              if (r.devolverFFEaPendientes.success) { success(r.devolverFFEaPendientes.message); cargarCotizaciones(); }
                              else error(r.devolverFFEaPendientes.message);
                            } catch (err) { error(err.message); }
                          }}
                          title="Devolver a pendientes para editar"
                          style={{ fontSize: '0.7rem', padding: '4px 8px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Devolver
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`¿Sincronizar cambios de "${cot.proyecto_nombre} ${cot.apartamento || ''}" al proyecto?\nEsto actualizará datos, capítulos y actividades del proyecto.`)) return;
                            try {
                              const r = await client.mutate(`mutation($cid: Int!, $src: String!) { sincronizarProyectoFFE(cotizacion_id: $cid, source: $src) { success message } }`, { cid: cot.id, src: cot.source || 'nueva' });
                              if (r.sincronizarProyectoFFE.success) { success(r.sincronizarProyectoFFE.message); cargarCotizaciones(); }
                              else error(r.sincronizarProyectoFFE.message);
                            } catch (err) { error(err.message); }
                          }}
                          title="Sincronizar cambios al proyecto existente"
                          style={{ fontSize: '0.7rem', padding: '4px 8px', background: '#059669', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Actualizar
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Reprocesar "${cot.proyecto_nombre} ${cot.apartamento || ''}"?\nEsto creará capítulos, cartera y cronograma si faltan.`)) return;
                            const enviarCorreo = window.confirm('¿Reenviar también el correo de notificación FFE?\n\nAceptar = Sí, enviar correo\nCancelar = No enviar correo');
                            try {
                              const r = await client.mutate(`mutation($cid: Int!, $src: String!, $ec: Boolean) { reprocesarFFE(cotizacion_id: $cid, source: $src, enviar_correo: $ec) { success message proyecto_id } }`, { cid: cot.id, src: cot.source || 'nueva', ec: enviarCorreo });
                              if (r.reprocesarFFE.success) success(r.reprocesarFFE.message + (enviarCorreo ? '' : ' (sin correo)'));
                              else error(r.reprocesarFFE.message);
                            } catch (err) { error(err.message); }
                          }}
                          title="Reprocesar: crear capítulos, cartera y cronograma faltantes"
                          style={{ fontSize: '0.7rem', padding: '4px 8px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Reprocesar
                        </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            className="btn-ver-ffe"
                            onClick={() => verDetalle(cot)}
                            title="Ver actividades FFE"
                          >
                            Ver FFE
                          </button>
                          {parseInt(cot.ffe_llaves) === 1 && parseInt(cot.ffe_pago) === 1 && parseInt(cot.ffe_firma) === 1 && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`¿Sincronizar cambios y enviar "${cot.proyecto_nombre} ${cot.apartamento || ''}" de vuelta a Exportados?\nEsto actualizará el proyecto con los cambios realizados.`)) return;
                                try {
                                  const r = await client.mutate(`mutation($cid: Int!, $src: String!) { sincronizarProyectoFFE(cotizacion_id: $cid, source: $src) { success message } }`, { cid: cot.id, src: cot.source || 'nueva' });
                                  if (r.sincronizarProyectoFFE.success) { success(r.sincronizarProyectoFFE.message); cargarCotizaciones(); }
                                  else error(r.sincronizarProyectoFFE.message);
                                } catch (err) { error(err.message); }
                              }}
                              title="Sincronizar cambios y enviar de vuelta a Exportados"
                              style={{ fontSize: '0.7rem', padding: '4px 8px', background: '#059669', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              Actualizar Proyecto
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  // ==============================
  // RENDER DETALLE DE ACTIVIDADES
  // ==============================
  const renderDetalle = () => (
    <>
      <div className="ffe-page-header">
        <div className="ffe-header-info">
          <h1>{tabActiva === 'ficha' ? 'Ficha FFE' : 'Contratistas'} - {cotizacionActual?.proyecto_nombre}</h1>
          <p>
            COT-{String(cotizacionActual?.consecutivo).padStart(4, '0')} |{' '}
            {cotizacionActual?.nombre_cliente} |{' '}
            Apto {cotizacionActual?.apartamento} |{' '}
            {actividades.length} actividades FFE
          </p>
        </div>
        <div className="ffe-header-actions">
          {modoLectura && (
            <span className="ffe-badge-enviado" style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
              Enviado a Proyectos — Solo lectura
            </span>
          )}
          {!modoLectura && (
            <>
              <span className={`autosave-indicator ${autoguardando ? 'saving' : ultimoAutoguardado ? 'saved' : ''}`}>
                {autoguardando ? 'Guardando...' : ultimoAutoguardado ? `Guardado ${ultimoAutoguardado.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}` : ''}
              </span>
              <button
                className="btn-guardar-config"
                onClick={guardarConfig}
                disabled={guardandoConfig}
              >
                {guardandoConfig ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </>
          )}
          {tabActiva !== 'carrito' && (
            <>
              {!modoLectura && (
                <button
                  className={`btn-toggle-panel ${vistaPanel === 'preview' ? 'active' : ''}`}
                  onClick={() => setVistaPanel(prev => prev === 'edicion' ? 'preview' : 'edicion')}
                >
                  {vistaPanel === 'edicion' ? 'Ver Vista Previa' : 'Ver Edición'}
                </button>
              )}
              <button
                className="btn-ver-ffe"
                onClick={() => setShowPdfModal(true)}
                disabled={descargando || descargandoContr}
              >
                {(descargando || descargandoContr) ? 'Generando...' : 'Descargar PDF'}
              </button>
            </>
          )}
          {!embebidoCotId && (
            <button className="btn-volver-ffe" onClick={volver}>
              Volver
            </button>
          )}
        </div>
      </div>

      {/* Pestañas */}
      <div className="ffe-tabs">
        {permisoTabFicha && (
        <button
          className={`ffe-tab ${tabActiva === 'ficha' ? 'ffe-tab-active' : ''}`}
          onClick={() => setTabActiva('ficha')}
        >
          Ficha FFE
        </button>
        )}
        {permisoTabContratistas && (
        <button
          className={`ffe-tab ${tabActiva === 'contratistas' ? 'ffe-tab-active' : ''}`}
          onClick={() => setTabActiva('contratistas')}
        >
          Contratistas
        </button>
        )}
        {permisoTabCarrito && (
        <button
          className={`ffe-tab ${tabActiva === 'carrito' ? 'ffe-tab-active' : ''}`}
          onClick={() => setTabActiva('carrito')}
        >
          Carrito de Compras
        </button>
        )}
        <button
          className={`ffe-tab ${tabActiva === 'planos_generados' ? 'ffe-tab-active' : ''}`}
          onClick={() => setTabActiva('planos_generados')}
        >
          Planos Generados
        </button>
        <button
          className={`ffe-tab ${tabActiva === 'adjuntos' ? 'ffe-tab-active' : ''}`}
          onClick={() => setTabActiva('adjuntos')}
        >
          Adjuntos
        </button>
      </div>

      {loading ? (
        <div className="ffe-loading">Cargando actividades FFE...</div>
      ) : (tabActiva === 'ficha' || tabActiva === 'contratistas') && cotizacionActual?.ffe_ficha_vacia ? (
        /* Ficha vaciada manualmente: Ficha FFE y Contratistas se muestran sin contenido */
        <div className="ffe-vacia-msg">
          Esta {tabActiva === 'ficha' ? 'Ficha FFE' : 'sección de Contratistas'} no tiene contenido para este proyecto.
        </div>
      ) : tabActiva === 'ficha' ? (
        /* ===== TAB: FICHA FFE ===== */
        (() => { const panelEfectivo = modoLectura ? 'preview' : vistaPanel; return (
        <div className={`ffe-split-layout ${panelEfectivo === 'preview' ? 'ffe-solo-preview' : 'ffe-solo-edicion'}`}>
          {panelEfectivo === 'edicion' && <div className="ffe-edit-panel">
            <div className="ffe-form-section">
              <div className="ffe-form-section-title">Planos</div>
              {planos.map((plano, idx) => (
                <div key={idx} className="ffe-plano-group">
                  <div className="ffe-plano-form-row">
                    <input type="text" value={plano.titulo} onChange={(e) => actualizarPlanoTitulo(idx, e.target.value)} placeholder="Título del plano" className="ffe-input ffe-plano-titulo-input" />
                    {planos.length > 1 && (
                      <button className="btn-eliminar-ffe" onClick={() => eliminarPlano(idx)} title="Eliminar recuadro">&#10005;</button>
                    )}
                  </div>
                  {plano.urls.map((url, uIdx) => (
                    <div key={uIdx} className="ffe-plano-form-row" style={{ paddingLeft: '12px', display: 'flex', gap: 6, alignItems: 'center' }}>
                      {url ? (
                        <span style={{ flex: 1, fontSize: '0.8rem', color: '#059669', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Imagen seleccionada</span>
                      ) : (
                        <span style={{ flex: 1, fontSize: '0.8rem', color: '#94a3b8' }}>Sin imagen</span>
                      )}
                      <button type="button" className="btn-catalogo-ffe" onClick={() => setCatalogoPlanoModal({ planoIdx: idx, urlIdx: uIdx })}>📂 Seleccionar del Catalogo</button>
                      <button
                        type="button"
                        className="btn-editar-plano-ffe"
                        disabled={!url}
                        title={url ? 'Editar plano (estilo Canva)' : 'Selecciona una imagen primero'}
                        onClick={() => {
                          if (!url || !cotizacionActual?.id || !plano.id) return;
                          // Persistir antes de abrir el editor (asegura que el UUID y la url esten guardados).
                          const proyNombre = cotizacionActual?.proyecto_nombre || '';
                          const apto = cotizacionActual?.apartamento || '';
                          const params = new URLSearchParams({
                            cotizacion_id: cotizacionActual.id,
                            recuadro_uuid: plano.id,
                            url_index: uIdx,
                            titulo: plano.titulo || '',
                            proyecto_nombre: proyNombre,
                            apartamento: apto,
                            imagen_base: url,
                          });
                          window.open(`/editor-planos?${params.toString()}`, '_blank');
                        }}
                        style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: '0.8rem', cursor: url ? 'pointer' : 'not-allowed', opacity: url ? 1 : 0.4 }}
                      >
                        🎨 Editar plano
                      </button>
                      {plano.urls.length > 1 && (
                        <button className="btn-eliminar-ffe" onClick={() => eliminarUrlDePlano(idx, uIdx)} title="Eliminar" style={{ width: '28px', height: '28px', fontSize: '0.8rem' }}>&#10005;</button>
                      )}
                    </div>
                  ))}
                  <button className="btn-agregar-url" onClick={() => agregarUrlAPlano(idx)}>+ Agregar imagen al plano</button>
                </div>
              ))}
              <button className="btn-guardar-ffe" onClick={agregarPlano}>+ Agregar recuadro de plano</button>

              {/* Planos generados en el editor (solo lectura): se reflejan aquí y en el PDF */}
              {planosGenRecuadros.length > 0 && (
                <div className="ffe-planos-generados">
                  <div className="ffe-planos-gen-title">Planos generados (se incluyen automáticamente en el PDF)</div>
                  <div className="ffe-planos-gen-grid">
                    {planosGenRecuadros.map((pg, i) => (
                      <div key={i} className="ffe-plano-gen-card">
                        <div className="ffe-plano-gen-nombre">{pg.titulo}</div>
                        <img src={pg.url} alt={pg.titulo} className="ffe-plano-gen-img" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="ffe-form-section">
              <div className="ffe-form-section-title">Consideraciones especiales</div>
              <textarea value={consideraciones || ''} onChange={(e) => setConsideraciones(e.target.value)} placeholder="Escriba las consideraciones especiales del proyecto..." className="ffe-input ffe-consideraciones-textarea" rows={3} />
            </div>

            {/* Cantidades Calculadas - Selección de enchapes */}
            <div className="ffe-form-section">
              <div className="ffe-form-section-title">Cantidades Calculadas — Selección de Enchapes</div>
              <table className="carrito-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Concepto</th>
                    <th>Cantidad</th>
                    <th>Und</th>
                    <th>Nota</th>
                    <th>Enchape</th>
                  </tr>
                </thead>
                <tbody>
                  {calcularCarrito().map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{item.nombre}</td>
                      <td className="carrito-valor">{item.valor.toFixed(2)}</td>
                      <td>{item.unidad}</td>
                      <td className="carrito-nota">{item.nota}</td>
                      <td>
                        <select
                          className="carrito-select-enchape"
                          value={(carritoData.observacionesCarrito || {})[item.nombre] || ''}
                          onChange={(e) => {
                            setCarritoData(prev => ({
                              ...prev,
                              observacionesCarrito: {
                                ...prev.observacionesCarrito,
                                [item.nombre]: e.target.value
                              }
                            }));
                          }}
                        >
                          <option value="">— Sin enchape —</option>
                          {listaEnchapes.map((enc, i) => {
                            const texto = [enc.ref, enc.color, enc.formato].filter(Boolean).join(' - ');
                            return <option key={i} value={texto}>{texto}</option>;
                          })}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pega y Lechadas - Editable */}
            <div className="ffe-form-section">
              <div className="ffe-form-section-title">Pega y Lechadas</div>
              {(() => {
                const items = calcularCarrito();
                const pisoGeneral = items.find(i => i.nombre === 'PISO GENERAL');
                const pegaFormula = pisoGeneral ? Math.round(pisoGeneral.valor / ((formulasDB && formulasDB['pega_m2_por_bulto']) || 2.4)) : 0;
                const pegaMostrada = carritoData.pegaManual ? carritoData.pega : pegaFormula;
                return (
                  <>
                    <div className="carrito-pega-row">
                      <label>Cantidad de bultos de pega:</label>
                      <input type="number" value={pegaMostrada}
                        onChange={(e) => setCarritoData(prev => ({ ...prev, pega: parseInt(e.target.value) || 0, pegaManual: true }))}
                        min="0" className="carrito-input-pega" />
                      <span className="carrito-pega-unit">bultos</span>
                      {carritoData.pegaManual && (
                        <button className="carrito-btn-reset-pega" onClick={() => setCarritoData(prev => ({ ...prev, pega: pegaFormula, pegaManual: false }))} title="Restaurar valor calculado">↺</button>
                      )}
                    </div>
                    {!carritoData.pegaManual && (
                      <div className="carrito-pega-formula">Calculado: Piso General ({pisoGeneral ? pisoGeneral.valor.toFixed(2) : 0} m²) ÷ ${(formulasDB && formulasDB['pega_m2_por_bulto']) || 2.4}</div>
                    )}

                    <div style={{ marginTop: '16px' }}>
                      <strong style={{ fontSize: '0.88rem', color: '#1e293b' }}>Lechadas</strong>
                      <table className="carrito-table carrito-lechadas-table" style={{ marginTop: '8px' }}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Color</th>
                            <th>Ubicación</th>
                          </tr>
                        </thead>
                        <tbody>
                          {carritoData.lechadas.map((lech, idx) => (
                            <tr key={idx}>
                              <td>Lechada {idx + 1}</td>
                              <td>
                                <input type="text" value={lech.color}
                                  onChange={(e) => {
                                    const nuevas = [...carritoData.lechadas];
                                    nuevas[idx] = { ...nuevas[idx], color: e.target.value };
                                    setCarritoData(prev => ({ ...prev, lechadas: nuevas }));
                                  }} placeholder="Color" className="carrito-input" />
                              </td>
                              <td>
                                <input type="text" value={lech.ubicacion}
                                  onChange={(e) => {
                                    const nuevas = [...carritoData.lechadas];
                                    nuevas[idx] = { ...nuevas[idx], ubicacion: e.target.value };
                                    setCarritoData(prev => ({ ...prev, lechadas: nuevas }));
                                  }} placeholder="Ubicación" className="carrito-input" />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>

            {actividades.length === 0 ? (
              <div className="ffe-empty">No hay actividades FFE en esta cotización</div>
            ) : (
              <div className="ffe-actividades">
                {actividadesPorEspacio.map(([espacio, acts]) => (
                  <div key={espacio} className="ffe-espacio-group">
                    <div className="ffe-espacio-title">{espacio}</div>
                    <div className="ffe-actividades-list">
                      {acts.map(act => (
                        <div key={act.id} className={`ffe-actividad-card${parseInt(act.activo_matriz) === 0 ? ' ffe-actividad-inactiva' : ''}`}>
                          <div className="ffe-imagen-zona">
                            {act.url_imagen ? (
                              <LazyProxyImage src={getProxyUrl(act.url_imagen)} alt={act.imagen_nombre || act.descripcion} className="ffe-imagen" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="ffe-imagen-placeholder">
                                <span className="ffe-placeholder-icon">&#128247;</span>
                                <span className="ffe-placeholder-text">Sin imagen</span>
                              </div>
                            )}
                            {act.imagen_nombre && (
                              <div className="ffe-imagen-info">
                                <span className="ffe-imagen-nombre">{act.imagen_nombre}</span>
                                {act.imagen_codigo && <span className="ffe-imagen-codigo">{act.imagen_codigo}</span>}
                              </div>
                            )}
                          </div>
                          <div className="ffe-descripcion">
                            {editandoDescAct === act.actividad_id ? (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                                <textarea
                                  value={descActTemp}
                                  onChange={(e) => setDescActTemp(e.target.value)}
                                  className="ffe-input"
                                  style={{ flex: 1, minHeight: 60, fontSize: '0.85rem' }}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) guardarDescripcionAct(act.actividad_id);
                                    if (e.key === 'Escape') cancelarEdicionDescAct();
                                  }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <button className="btn-cant-ok" onClick={() => guardarDescripcionAct(act.actividad_id)} title="Guardar (Ctrl+Enter)">&#10003;</button>
                                  <button className="btn-cant-cancel" onClick={cancelarEdicionDescAct} title="Cancelar (Esc)">&#10005;</button>
                                </div>
                              </div>
                            ) : (
                              <p
                                className="ffe-desc-text ffe-campo-editable"
                                onClick={() => iniciarEdicionDescAct(act)}
                                title="Clic para editar — se sincronizará en la cotización"
                                style={{ cursor: 'pointer' }}
                              >
                                {act.descripcion}
                              </p>
                            )}
                          </div>
                          <div className="ffe-cantidad">
                            {editandoCantidad === act.actividad_id ? (
                              <div className="ffe-cantidad-edit">
                                <input type="number" value={cantidadTemp} onChange={(e) => setCantidadTemp(e.target.value)} className="ffe-cantidad-input" min="0.01" step="0.01" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') guardarCantidad(act.actividad_id); if (e.key === 'Escape') cancelarEdicionCantidad(); }} />
                                <div className="ffe-cantidad-btns">
                                  <button className="btn-cant-ok" onClick={() => guardarCantidad(act.actividad_id)}>&#10003;</button>
                                  <button className="btn-cant-cancel" onClick={cancelarEdicionCantidad}>&#10005;</button>
                                </div>
                              </div>
                            ) : (
                              <span className="ffe-cant-valor ffe-cant-editable" onClick={() => iniciarEdicionCantidad(act)} title="Clic para editar cantidad">{act.cantidad}</span>
                            )}
                            <span className="ffe-cant-unidad">{act.unidad}</span>
                          </div>
                          <div className="ffe-col-observaciones">
                            <div className="ffe-obs-content" onClick={() => editandoCampo?.id !== act.actividad_id || editandoCampo?.campo !== 'observaciones' ? iniciarEdicionCampo(act.actividad_id, 'observaciones', act.observaciones) : null} title="Clic para editar observaciones">
                              {editandoCampo?.id === act.actividad_id && editandoCampo?.campo === 'observaciones' ? (
                                <input
                                  type="text"
                                  className="ffe-inline-input"
                                  value={campoTemp}
                                  onChange={(e) => setCampoTemp(e.target.value)}
                                  onBlur={() => guardarCampoInline(act.actividad_id, 'observaciones', campoTemp)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') guardarCampoInline(act.actividad_id, 'observaciones', campoTemp); if (e.key === 'Escape') cancelarEdicionCampo(); }}
                                  autoFocus
                                  placeholder="Observaciones..."
                                />
                              ) : (
                                <span className={`ffe-campo-editable ${act.observaciones ? '' : 'ffe-campo-vacio'}`}>
                                  {act.observaciones || 'Observaciones'}
                                </span>
                              )}
                            </div>
                            {(act.enchape || '').toUpperCase() === 'SI' && (
                              <button
                                className="ffe-btn-enchape"
                                onClick={(e) => { e.stopPropagation(); setModalEnchape(act.actividad_id); setBusquedaEnchape(''); }}
                                title="Seleccionar enchape"
                              >
                                &#9638;
                              </button>
                            )}
                          </div>
                          <div className="ffe-col-proveedor" onClick={() => editandoCampo?.id !== act.actividad_id || editandoCampo?.campo !== 'proveedor' ? iniciarEdicionCampo(act.actividad_id, 'proveedor', act.proveedor) : null} title="Clic para seleccionar proveedor">
                            {editandoCampo?.id === act.actividad_id && editandoCampo?.campo === 'proveedor' ? (
                              <select
                                className="ffe-inline-select"
                                value={campoTemp}
                                onChange={(e) => {
                                  setCampoTemp(e.target.value);
                                  guardarCampoInline(act.actividad_id, 'proveedor', e.target.value);
                                }}
                                onBlur={() => cancelarEdicionCampo()}
                                autoFocus
                              >
                                <option value="">-- Seleccionar --</option>
                                {listaProveedores.map(prov => (
                                  <option key={prov.id} value={prov.nombre}>{prov.nombre}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`ffe-campo-editable ${act.proveedor ? '' : 'ffe-campo-vacio'}`}>
                                {act.proveedor || 'Proveedor'}
                              </span>
                            )}
                          </div>
                          <div className="ffe-acciones">
                            {editandoActividad === act.actividad_id ? (
                              <div className="ffe-form-imagen">
                                <input type="text" placeholder="Nombre del producto" value={formImagen.nombre} onChange={(e) => setFormImagen(prev => ({ ...prev, nombre: e.target.value }))} className="ffe-input" />
                                <input type="text" placeholder="Codigo" value={formImagen.codigo} onChange={(e) => setFormImagen(prev => ({ ...prev, codigo: e.target.value }))} className="ffe-input" />
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <button type="button" className="btn-catalogo-ffe" onClick={() => setCatalogoModal(act.actividad_id)} title="Buscar en Catalogo FFE">📂 Seleccionar del Catalogo</button>
                                  {formImagen.url_imagen && <span style={{ fontSize: '0.75rem', color: '#059669' }}>Imagen seleccionada</span>}
                                </div>
                                <div className="ffe-form-buttons">
                                  <button className="btn-guardar-ffe" onClick={() => guardarImagen(act.actividad_id)} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar'}</button>
                                  <button className="btn-cancelar-ffe" onClick={cancelarEdicion}>Cancelar</button>
                                </div>
                              </div>
                            ) : (
                              <div className="ffe-btn-group">
                                <button className="btn-editar-ffe" onClick={() => iniciarEdicion(act)} title="Editar imagen">{'\u270E'}</button>
                                {act.url_imagen && (
                                  <button className="btn-eliminar-ffe" onClick={() => eliminarImagen(act.actividad_id)} title="Eliminar imagen">{'\u2715'}</button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>}

          <div className="ffe-preview-panel" style={panelEfectivo !== 'preview' ? { position: 'absolute', left: '-99999px', top: 0 } : {}}>
            <div className="ffe-preview-sticky">
              <FichaFFEPDF
                ref={fichaRef}
                cotizacion={cotizacionActual}
                actividades={actividades}
                planos={[...planos, ...planosGenRecuadros]}
                consideraciones={consideraciones}
                carrito={(() => {
                  // Los mismos bultos de pega que se ven arriba: los manuales o el calculado
                  const pisoGeneral = calcularCarrito().find(i => i.nombre === 'PISO GENERAL');
                  const pegaFormula = pisoGeneral ? Math.round(pisoGeneral.valor / ((formulasDB && formulasDB['pega_m2_por_bulto']) || 2.4)) : 0;
                  return { ...carritoData, pega: carritoData.pegaManual ? carritoData.pega : pegaFormula };
                })()}
              />
            </div>
          </div>
        </div>
        ); })()
      ) : tabActiva === 'contratistas' ? (
        /* ===== TAB: CONTRATISTAS ===== */
        (() => { const panelEfectivo = modoLectura ? 'preview' : vistaPanel; return (
        <div className={`ffe-split-layout ${panelEfectivo === 'preview' ? 'ffe-solo-preview' : 'ffe-solo-edicion'}`}>
          {panelEfectivo === 'edicion' && <div className="ffe-edit-panel">
            {/* Datos Residentes a cargo */}
            <div className="ffe-form-section">
              <div className="ffe-form-section-title">Datos Residentes a cargo</div>
              <div className="ffe-residente-fields">
                <div className="ffe-residente-row">
                  <label className="ffe-residente-label">Nombre</label>
                  <div className="ffe-residente-search-wrapper" ref={dropdownResidenteRef}>
                    <input
                      type="text"
                      value={mostrarDropdownResidente ? busquedaResidente : residente.nombre}
                      onChange={(e) => {
                        setBusquedaResidente(e.target.value);
                        setMostrarDropdownResidente(true);
                      }}
                      onFocus={() => {
                        setBusquedaResidente(residente.nombre);
                        setMostrarDropdownResidente(true);
                      }}
                      placeholder="Buscar residente..."
                      className="ffe-input"
                    />
                    {mostrarDropdownResidente && (
                      <div className="ffe-residente-dropdown">
                        {residentesFiltrados.length > 0 ? (
                          residentesFiltrados.map(r => (
                            <div
                              key={r.id}
                              className="ffe-residente-option"
                              onClick={() => seleccionarResidente(r)}
                            >
                              <span className="ffe-residente-option-nombre">{r.nombre}</span>
                              <span className="ffe-residente-option-tel">{r.telefono || ''}</span>
                            </div>
                          ))
                        ) : (
                          <div className="ffe-residente-option ffe-residente-no-results">Sin resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="ffe-residente-row">
                  <label className="ffe-residente-label">Contacto</label>
                  <input type="text" value={residente.contacto} onChange={(e) => setResidente(prev => ({ ...prev, contacto: e.target.value }))} placeholder="Teléfono o correo" className="ffe-input" />
                </div>
                <div className="ffe-residente-row">
                  <label className="ffe-residente-label">Empresa</label>
                  <input type="text" value={residente.empresa} onChange={(e) => setResidente(prev => ({ ...prev, empresa: e.target.value }))} placeholder="Empresa del residente" className="ffe-input" />
                </div>
              </div>
            </div>

            {/* Info: planos y consideraciones vienen de Ficha FFE */}
            <div className="ffe-form-section">
              <div className="ffe-form-section-title">Planos</div>
              <p className="ffe-sync-note">Los planos se configuran desde la pestaña Ficha FFE y se sincronizan automáticamente.</p>
              <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>{planos.length} plano(s) configurados con {planos.reduce((acc, p) => acc + p.urls.filter(u => u).length, 0)} imagen(es)</p>
            </div>

            <div className="ffe-form-section">
              <div className="ffe-form-section-title">Consideraciones especiales</div>
              <p className="ffe-sync-note">Las consideraciones se configuran desde la pestaña Ficha FFE y se sincronizan automáticamente.</p>
              {consideraciones && <p style={{ fontSize: '0.85rem', color: '#374151', fontStyle: 'italic' }}>"{consideraciones}"</p>}
            </div>

            <div className="ffe-form-section">
              <div className="ffe-form-section-title">Actividades por capítulo</div>
              <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>{actividadesTodas.length} actividades contratadas incluidas en el documento</p>
            </div>
          </div>}

          <div className="ffe-preview-panel" style={panelEfectivo !== 'preview' ? { position: 'absolute', left: '-99999px', top: 0 } : {}}>
            <div className="ffe-preview-sticky">
              <ContratistaPDF ref={contratistaRef} cotizacion={cotizacionActual} actividades={actividadesTodas} planos={planos} consideraciones={consideraciones} residente={residente} />
            </div>
          </div>
        </div>
        ); })()
      ) : tabActiva === 'planos_generados' ? (
        /* ===== TAB: PLANOS GENERADOS ===== */
        <PlanosGeneradosTab cotizacion={cotizacionActual} planos={planos} onCambio={() => cotizacionActual?.id && cargarPlanosGen(cotizacionActual.id)} />
      ) : tabActiva === 'adjuntos' ? (
        /* ===== TAB: ADJUNTOS ===== */
        <AdjuntosFfeTab cotizacion={cotizacionActual} />
      ) : (
        /* ===== TAB: CARRITO DE COMPRAS ===== */
        <div className="carrito-compras-container">
          {/* Datos del Inmueble - full width */}
          <div className="carrito-section carrito-section-full">
            <div className="carrito-section-title">Datos del Inmueble</div>
            <div className="carrito-datos-grid">
              <div className="carrito-dato"><span>Área Apto:</span> <strong>{cotizacionActual?.area_m2 || 0} m²</strong></div>
              <div className="carrito-dato"><span>Baño Social:</span> <strong>{cotizacionActual?.bano_social_alto || 0} × {cotizacionActual?.bano_social_ancho || 0} m = {((parseFloat(cotizacionActual?.bano_social_alto) || 0) * (parseFloat(cotizacionActual?.bano_social_ancho) || 0)).toFixed(2)} m²</strong></div>
              <div className="carrito-dato"><span>Baño Ppal:</span> <strong>{cotizacionActual?.bano_ppal_alto || 0} × {cotizacionActual?.bano_ppal_ancho || 0} m = {((parseFloat(cotizacionActual?.bano_ppal_alto) || 0) * (parseFloat(cotizacionActual?.bano_ppal_ancho) || 0)).toFixed(2)} m²</strong></div>
              <div className="carrito-dato"><span>Balcón:</span> <strong>{cotizacionActual?.balcon || 0} m²</strong></div>
              <div className="carrito-dato"><span>Altura Ducha:</span> <strong>{cotizacionActual?.altura_ducha || 0} m</strong></div>
            </div>
          </div>

          {/* Col izq: Cantidades Calculadas (solo lectura) */}
          <div className="carrito-section">
            <div className="carrito-section-title">Cantidades Calculadas</div>
            <table className="carrito-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Concepto</th>
                  <th>Cantidad</th>
                  <th>Und</th>
                  <th>Nota</th>
                  <th>Enchape Asignado</th>
                </tr>
              </thead>
              <tbody>
                {calcularCarrito().map((item, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{item.nombre}</td>
                    <td className="carrito-valor">{item.valor.toFixed(2)}</td>
                    <td>{item.unidad}</td>
                    <td className="carrito-nota">{item.nota}</td>
                    <td className="carrito-enchape-asignado">
                      {carritoData.observacionesCarrito[item.nombre] || <span className="carrito-sin-enchape">Sin asignar</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Col der: Pega + Lechadas (solo lectura) */}
          {(() => {
            const items = calcularCarrito();
            const pisoGeneral = items.find(i => i.nombre === 'PISO GENERAL');
            const pegaFormula = pisoGeneral ? Math.round(pisoGeneral.valor / ((formulasDB && formulasDB['pega_m2_por_bulto']) || 2.4)) : 0;
            const pegaMostrada = carritoData.pegaManual ? carritoData.pega : pegaFormula;
            return (
          <div className="carrito-section">
            <div className="carrito-section-title">Pega</div>
            <div className={`carrito-pega-row ${carritoData.checks.pega ? 'carrito-item-done' : ''}`}>
              <input type="checkbox" className="carrito-check" checked={!!carritoData.checks.pega}
                onChange={() => {
                  const nuevo = { ...carritoData, checks: { ...carritoData.checks, pega: !carritoData.checks.pega } };
                  setCarritoData(nuevo);
                  toggleCheck(null, null, nuevo);
                }} />
              <label>Cantidad de bultos:</label>
              <strong className="carrito-readonly-value">{pegaMostrada} bultos</strong>
            </div>

            <div className="carrito-section-title" style={{ marginTop: '1rem' }}>Lechadas</div>
            <table className="carrito-table carrito-lechadas-table">
              <thead>
                <tr>
                  <th style={{ width: '28px' }}></th>
                  <th>#</th>
                  <th>Color</th>
                  <th>Ubicación</th>
                </tr>
              </thead>
              <tbody>
                {carritoData.lechadas.map((lech, idx) => {
                  const checked = !!(carritoData.checks.lechadas[idx]);
                  return (
                  <tr key={idx} className={checked ? 'carrito-item-done' : ''}>
                    <td>
                      <input type="checkbox" className="carrito-check" checked={checked}
                        onChange={() => {
                          const nuevasChecks = [...(carritoData.checks.lechadas || [])];
                          nuevasChecks[idx] = !checked;
                          const nuevo = { ...carritoData, checks: { ...carritoData.checks, lechadas: nuevasChecks } };
                          setCarritoData(nuevo);
                          toggleCheck(null, null, nuevo);
                        }} />
                    </td>
                    <td>Lechada {idx + 1}</td>
                    <td className="carrito-enchape-asignado">{lech.color || <span className="carrito-sin-enchape">-</span>}</td>
                    <td className="carrito-enchape-asignado">{lech.ubicacion || <span className="carrito-sin-enchape">-</span>}</td>
                  </tr>
                  ); })}
              </tbody>
            </table>
          </div>
          ); })()}

          {/* Resumen de Enchapes seleccionados - full width */}
          {(() => {
            // Sumar M2 de enchapes desde observacionesCarrito (Cantidades Calculadas)
            const itemsCalc = calcularCarrito();
            const enchapeSumas = {};
            Object.entries(carritoData.observacionesCarrito || {}).forEach(([itemNombre, encTexto]) => {
              if (!encTexto) return;
              const itemCalc = itemsCalc.find(i => i.nombre === itemNombre);
              const valorM2 = itemCalc ? itemCalc.valor : 0;
              if (!enchapeSumas[encTexto]) {
                // Buscar datos del enchape en la lista
                const enc = listaEnchapes.find(e => {
                  const t = [e.ref, e.color, e.formato].filter(Boolean).join(' - ');
                  return t === encTexto;
                });
                enchapeSumas[encTexto] = { ref: enc?.ref || encTexto, color: enc?.color || '', formato: enc?.formato || '', total: 0 };
              }
              enchapeSumas[encTexto].total += valorM2;
            });
            const enchapesUsados = Object.values(enchapeSumas).filter(e => e.total > 0);
            if (enchapesUsados.length === 0) return null;
            return (
              <div className="carrito-section carrito-section-full">
                <div className="carrito-section-title">Enchapes Seleccionados ({enchapesUsados.length})</div>
                <table className="carrito-table">
                  <thead>
                    <tr>
                      <th style={{ width: '28px' }}></th>
                      <th>#</th>
                      <th>Referencia</th>
                      <th>Color</th>
                      <th>Formato</th>
                      <th>Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enchapesUsados.map((enc, idx) => {
                      const encKey = [enc.ref, enc.color, enc.formato].filter(Boolean).join(' - ');
                      const checked = !!(carritoData.checks.enchapes[encKey]);
                      return (
                      <tr key={idx} className={checked ? 'carrito-item-done' : ''}>
                        <td>
                          <input type="checkbox" className="carrito-check" checked={checked}
                            onChange={() => {
                              const nuevo = { ...carritoData, checks: { ...carritoData.checks, enchapes: { ...carritoData.checks.enchapes, [encKey]: !checked } } };
                              setCarritoData(nuevo);
                              toggleCheck(null, null, nuevo);
                            }}
                          />
                        </td>
                        <td>{idx + 1}</td>
                        <td>{enc.ref}</td>
                        <td>{enc.color}</td>
                        <td>{enc.formato}</td>
                        <td style={{ textAlign: 'center' }}>{enc.total.toFixed(2)} M2</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Actividades a comprar (Req. Compra = SI en la matriz), agrupadas por proveedor */}
          {(() => {
            if (actividadesCompra === null) {
              return <div className="carrito-section carrito-section-full"><div className="carrito-section-title">Compras por proveedor</div><p className="carrito-compras-vacio">Cargando...</p></div>;
            }
            const SIN = 'Sin proveedor asignado';
            const grupos = {};
            actividadesCompra.forEach(a => {
              const g = a.proveedor || SIN;
              (grupos[g] = grupos[g] || []).push(a);
            });
            const nombres = Object.keys(grupos).filter(g => g !== SIN).sort((a, b) => a.localeCompare(b, 'es'));
            if (grupos[SIN]) nombres.push(SIN);
            // Las compras se guardan en el mismo registro del Carrito de Compras General
            const marcar = async (ids, valor) => {
              const antes = actividadesCompra;
              const set = new Set(ids);
              setActividadesCompra(prev => prev.map(a => set.has(a.id) ? { ...a, comprado: valor } : a));
              try {
                const r = await client.mutate(`mutation($items: [CompraCarritoInput!]!, $c: Boolean!) { marcarComprasCarrito(items: $items, comprado: $c) { success message } }`,
                  { items: ids.map(id => ({ cotizacion_id: cotizacionActual.id, source: cotizacionActual.source || 'nueva', linea_id: id })), c: valor });
                if (!r.marcarComprasCarrito.success) { setActividadesCompra(antes); error(r.marcarComprasCarrito.message); }
              } catch { setActividadesCompra(antes); error('No se pudo guardar la compra'); }
            };
            return (
              <div className="carrito-section carrito-section-full">
                <div className="carrito-section-title">
                  Compras por proveedor ({actividadesCompra.length} {actividadesCompra.length === 1 ? 'actividad' : 'actividades'})
                </div>
                {actividadesCompra.length === 0 && (
                  <p className="carrito-compras-vacio">Esta cotización no tiene actividades marcadas con Req. Compra = SI en la matriz.</p>
                )}
                {nombres.map(nombre => {
                  const items = grupos[nombre];
                  const hechos = items.filter(a => a.comprado).length;
                  const todos = hechos === items.length;
                  return (
                    <div key={nombre} className={`carrito-proveedor-grupo ${nombre === SIN ? 'sin-proveedor' : ''}`}>
                      <div className={`carrito-proveedor-header ${todos ? 'carrito-item-done' : ''}`}>
                        <input type="checkbox" className="carrito-check" checked={todos}
                          title={todos ? 'Desmarcar todas' : 'Marcar todas como compradas'}
                          onChange={() => marcar(items.map(a => a.id), !todos)} />
                        <span className="carrito-proveedor-nombre">{nombre}</span>
                        <span className="carrito-proveedor-conteo">{hechos}/{items.length}</span>
                      </div>
                      <table className="carrito-table">
                        <thead>
                          <tr>
                            <th style={{ width: '28px' }}></th>
                            <th>#</th>
                            <th>Código</th>
                            <th>Descripción</th>
                            <th>Espacio</th>
                            <th>Cant</th>
                            <th>Und</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((act, idx) => {
                            const checked = !!act.comprado;
                            return (
                              <tr key={act.id} className={checked ? 'carrito-item-done' : ''}>
                                <td>
                                  <input type="checkbox" className="carrito-check" checked={checked}
                                    disabled={checked && !act.comprado_manual}
                                    title={checked && !act.comprado_manual ? 'Comprado desde el Carrito de Compras General' : ''}
                                    onChange={() => marcar([act.id], !checked)} />
                                </td>
                                <td>{idx + 1}</td>
                                <td className="carrito-codigo">{act.codigo}</td>
                                <td>
                                  {act.descripcion}
                                  {act.comprado_manual && act.comprado_por && (
                                    <div className="carrito-comprado-info">Comprado por {act.comprado_por}{act.comprado_at ? ` · ${new Date(act.comprado_at).toLocaleDateString('es-CO')}` : ''}</div>
                                  )}
                                  {Number(act.cantidad_comprada) > 0 && (
                                    <div className="carrito-comprado-info">En compra {(act.compras_ids || []).map(id => `#${id}`).join(', ')} · {act.cantidad_comprada} de {act.cantidad}</div>
                                  )}
                                </td>
                                <td>{act.espacio}</td>
                                <td className="carrito-valor">{act.cantidad}</td>
                                <td>{act.unidad}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Botón guardar - full width */}
          <div className="carrito-actions">
            <button
              className="btn-guardar-carrito"
              onClick={guardarCarrito}
              disabled={guardandoCarrito}
            >
              {guardandoCarrito ? 'Guardando...' : 'Guardar Carrito'}
            </button>
          </div>
        </div>
      )}
    </>
  );

  // Enchapes filtrados para el modal
  const enchapesFiltrados = listaEnchapes.filter(e => {
    if (!busquedaEnchape) return true;
    const q = busquedaEnchape.toLowerCase();
    return (e.ref || '').toLowerCase().includes(q) ||
           (e.color || '').toLowerCase().includes(q) ||
           (e.formato || '').toLowerCase().includes(q);
  });

  return (
    <div className="ficha-ffe-container">
      {vista === 'lista' ? renderLista() : renderDetalle()}

      {/* Modal de enchapes */}
      {modalEnchape && (
        <div className="enchape-modal-overlay" onClick={() => { setModalEnchape(null); setBusquedaEnchape(''); }}>
          <div className="enchape-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enchape-modal-header">
              <h3>Seleccionar Enchape</h3>
              <button className="enchape-modal-close" onClick={() => { setModalEnchape(null); setBusquedaEnchape(''); }}>&times;</button>
            </div>
            <div className="enchape-modal-search">
              <input
                type="text"
                placeholder="Buscar por ref, color o formato..."
                value={busquedaEnchape}
                onChange={(e) => setBusquedaEnchape(e.target.value)}
                autoFocus
              />
            </div>
            <div className="enchape-modal-list">
              {enchapesFiltrados.length === 0 ? (
                <div className="enchape-modal-empty">No se encontraron enchapes</div>
              ) : (
                enchapesFiltrados.map(enchape => (
                  <div
                    key={enchape.id}
                    className="enchape-modal-item"
                    onClick={() => seleccionarEnchape(enchape, modalEnchape)}
                  >
                    <span className="enchape-modal-ref">{enchape.ref}</span>
                    <span className="enchape-modal-color">{enchape.color}</span>
                    <span className="enchape-modal-formato">{enchape.formato}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmación envío a proyectos */}
      {modalEnvioProyecto && (
        <div className="ffe-confirm-overlay" onClick={() => setModalEnvioProyecto(null)}>
          <div className="ffe-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ffe-confirm-icon">&#9888;</div>
            <h3>Enviar a Proyectos</h3>
            <p>Al activar los 3 requisitos (Llaves, Pago y Firma), esta ficha FFE se enviará automáticamente a la lista de Proyectos con todos sus datos.</p>
            <p className="ffe-confirm-note">Esta acción no es reversible. Una vez enviada, la ficha quedará bloqueada en modo solo lectura.</p>
            <div className="ffe-confirm-buttons">
              <button className="ffe-confirm-cancel" onClick={() => setModalEnvioProyecto(null)}>Cancelar</button>
              <button className="ffe-confirm-ok" onClick={async () => {
                const { cotizacionId, source, campo } = modalEnvioProyecto;
                setModalEnvioProyecto(null);
                await ejecutarToggleFFE(cotizacionId, source, campo);
              }}>Confirmar envío</button>
            </div>
          </div>
        </div>
      )}
      {/* MODAL: Tipo de PDF */}
      {showPdfModal && (
        <div className="mkt-modal-overlay" onClick={() => setShowPdfModal(false)}>
          <div className="ffe-pdf-modal" onClick={e => e.stopPropagation()}>
            <h3>Descargar PDF</h3>
            <p style={{color:'#6b7280',fontSize:'0.85rem',margin:'0 0 20px'}}>¿Para qué necesitas el PDF?</p>
            <div className="ffe-pdf-options">
              <button className="ffe-pdf-option enviar" onClick={() => {
                setShowPdfModal(false);
                tabActiva === 'ficha' ? descargarPDF() : descargarPDFContratistas();
              }}>
                <span className="ffe-pdf-option-icon">📧</span>
                <span className="ffe-pdf-option-title">Para Enviar</span>
                <span className="ffe-pdf-option-desc">PDF continuo, ideal para compartir digitalmente</span>
              </button>
              <button className="ffe-pdf-option imprimir" onClick={() => {
                descargarPDFPaginado(tabActiva === 'ficha' ? 'ficha' : 'contratistas');
              }}>
                <span className="ffe-pdf-option-icon">🖨️</span>
                <span className="ffe-pdf-option-title">Para Imprimir</span>
                <span className="ffe-pdf-option-desc">PDF paginado, sin contenido cortado entre páginas</span>
              </button>
            </div>
            <button className="ffe-pdf-cancel" onClick={() => setShowPdfModal(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Modal Catalogo FFE para seleccionar imagen de actividad */}
      {catalogoModal && (
        <div className="cat-modal-overlay" onClick={() => setCatalogoModal(null)}>
          <div className="cat-modal-content cat-modal-xl" onClick={e => e.stopPropagation()}>
            <div className="cat-modal-header">
              <h3>Seleccionar Imagen del Catalogo FFE</h3>
              <button className="cat-modal-close" onClick={() => setCatalogoModal(null)}>&times;</button>
            </div>
            <div className="cat-modal-body">
              <CatalogoFFE
                selectionMode={true}
                bucket="catalogo-ffe"
                allowBucketSwitch={true}
                onSelectImage={(url, key, name) => {
                  setFormImagen(prev => ({ ...prev, url_imagen: url, nombre: prev.nombre || name.replace(/\.[^.]+$/, '') }));
                  setCatalogoModal(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Catalogo Planos para seleccionar imagen de plano */}
      {catalogoPlanoModal && (
        <div className="cat-modal-overlay" onClick={() => setCatalogoPlanoModal(null)}>
          <div className="cat-modal-content cat-modal-xl" onClick={e => e.stopPropagation()}>
            <div className="cat-modal-header">
              <h3>Seleccionar Imagen de Plano</h3>
              <button className="cat-modal-close" onClick={() => setCatalogoPlanoModal(null)}>&times;</button>
            </div>
            <div className="cat-modal-body">
              <CatalogoFFE
                selectionMode={true}
                bucket="planos"
                allowBucketSwitch={true}
                onSelectImage={(url, key, name) => {
                  actualizarPlanoUrl(catalogoPlanoModal.planoIdx, catalogoPlanoModal.urlIdx, url);
                  setCatalogoPlanoModal(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FichaFFE;
